/* TaskFlow v7 - Mobile Responsive + Role Selection in Member Creation */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, doc, setDoc, deleteDoc, onSnapshot } from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyAjHqqavRURQ1KH-RpJ50Tp5CdBmAKkLq8",
  authDomain: "taskflow-avgroup.firebaseapp.com",
  databaseURL: "https://taskflow-avgroup-default-rtdb.firebaseio.com",
  projectId: "taskflow-avgroup",
  storageBucket: "taskflow-avgroup.firebasestorage.app",
  messagingSenderId: "916759111751",
  appId: "1:916759111751:web:29ab54b6cf4592ab692962",
  measurementId: "G-C80GD3Q762"
};

const firebaseApp = initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);

/* ── Global responsive CSS injected once at app level ── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { -webkit-text-size-adjust: 100%; }
  body { font-family: 'Sora','Segoe UI',sans-serif; background: #f5f6fa; }
  input, select, textarea, button { font-family: inherit; }
  a { text-decoration: none; }
  input:focus, select:focus, textarea:focus {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59,130,246,.12) !important;
    outline: none;
  }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: #f1f5f9; }
  ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes popIn   { from { opacity:0; transform: scale(.95) translateY(10px); } to { opacity:1; transform: scale(1) translateY(0); } }
  @keyframes drift   { 0%,100%{ transform:translateY(0) scale(1); } 50%{ transform:translateY(-28px) scale(1.05); } }
  @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }

  /* ── Responsive grid helpers ── */
  .tf-stats   { display:grid; grid-template-columns:repeat(5,1fr); gap:12px; margin-bottom:24px; }
  .tf-toolbar { display:flex; align-items:center; gap:8px; flex-wrap:wrap; background:#fff; border-radius:16px; padding:12px 16px; border:1.5px solid #f0f0f0; margin-bottom:18px; }
  .tf-grid    { display:grid; gap:12px; grid-template-columns:repeat(auto-fill,minmax(340px,1fr)); }
  .tf-modal-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .tf-quick-grid { display:grid; grid-template-columns:1fr 1fr 1fr 1fr; gap:10px; }
  .tf-user-row { display:flex; align-items:center; gap:16px; flex-wrap:wrap; }
  .tf-user-stats { display:flex; gap:14px; }
  .tf-user-actions { display:flex; gap:8px; flex-wrap:wrap; }
  .tf-header-inner { max-width:1400px; margin:0 auto; padding:0 20px; display:flex; align-items:center; justify-content:space-between; height:62px; gap:10px; }
  .tf-main { max-width:1400px; margin:0 auto; padding:24px 20px; }
  .tf-nav { display:flex; background:#f1f5f9; border-radius:10px; padding:3px; gap:2px; }

  @media (max-width: 768px) {
    .tf-stats   { grid-template-columns: repeat(3,1fr); }
    .tf-grid    { grid-template-columns: 1fr; }
    .tf-modal-grid { grid-template-columns: 1fr; }
    .tf-quick-grid { grid-template-columns: 1fr 1fr; }
    .tf-user-row { flex-direction:column; align-items:flex-start; }
    .tf-user-stats { width:100%; justify-content:space-between; }
    .tf-user-actions { width:100%; }
    .tf-header-inner { height:auto; padding:10px 16px; flex-wrap:wrap; gap:8px; }
    .tf-main { padding:16px 12px; }
    .tf-nav button { padding:6px 10px !important; font-size:11px !important; }
    .tf-toolbar { padding:10px 12px; gap:6px; }
  }
  @media (max-width: 480px) {
    .tf-stats   { grid-template-columns: repeat(2,1fr); }
    .tf-quick-grid { grid-template-columns: 1fr; }
    .tf-user-actions { flex-direction:column; }
    .tf-user-actions button { width:100%; justify-content:center; }
  }
`;

const DB = {
  async set(col, id, data) {
    try {
      if (data === null) { await deleteDoc(doc(db, col, id)); return; }
      await setDoc(doc(db, col, id), data);
    } catch (e) { console.error("DB.set error:", e); }
  },
  async del(col, id) {
    try { await deleteDoc(doc(db, col, id)); } catch (e) { console.error("DB.del error:", e); }
  },
  listen(col, callback) {
    return onSnapshot(collection(db, col), snap => {
      callback(snap.docs.map(d => ({ ...d.data() })));
    });
  },
};

const ADMIN = { id: "admin", role: "admin", name: "Admin", email: "admin@taskflow.com", password: "Admin@2024" };

const PRIORITY_CFG = {
  High:   { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  Medium: { color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  Low:    { color: "#059669", bg: "#f0fdf4", border: "#a7f3d0" },
};

const STATUS_CFG = {
  "Draft":       { color: "#78716c", bg: "#fafaf9", border: "#e7e5e4" },
  "To-Do":       { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
  "In Progress": { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  "Hold":        { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  "Review":      { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  "Completed":   { color: "#059669", bg: "#f0fdf4", border: "#a7f3d0" },
};

const ALL_STATUSES = ["Draft", "To-Do", "In Progress", "Hold", "Review", "Completed"];

const today   = () => new Date().toISOString().split("T")[0];
const nowISO  = () => new Date().toISOString();
const uid     = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const fmtDate = (d) => !d ? "-" : new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const fmtTime = (iso) => !iso ? "-" : new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true });

// Net duration = total elapsed minus any paused time
const calcNetMs = (t) => {
  if (!t.startTime || !t.endTime) return null;
  // Draft and Hold time are NOT counted toward duration
  const totalMs  = new Date(t.endTime) - new Date(t.startTime);
  const pausedMs = (t.pausedDuration || 0) + (t.draftDuration || 0);
  return Math.max(0, totalMs - pausedMs);
};
const fmtDuration = (startISO, endISO, pausedMs = 0) => {
  if (!startISO || !endISO) return "-";
  const ms = Math.max(0, new Date(endISO) - new Date(startISO) - pausedMs);
  const totalMins = Math.floor(ms / 60000);
  const days  = Math.floor(totalMins / 1440);
  const hrs   = Math.floor((totalMins % 1440) / 60);
  const mins  = totalMins % 60;
  if (days > 0)  return `${days}d ${hrs}h ${mins}m`;
  if (hrs > 0)   return `${hrs}h ${mins}m`;
  return `${mins}m`;
};
const isOver  = (t) => t.deadline && t.status !== "Completed" && t.status !== "Hold" && new Date(t.deadline + "T00:00:00") < new Date(today() + "T00:00:00");

/* Session persistence - survives page refresh */
const SESSION_KEY = "tf_session_user";
const saveSession  = (user) => { try { localStorage.setItem(SESSION_KEY, JSON.stringify(user)); } catch(e) {} };
const loadSession  = ()     => { try { const v = localStorage.getItem(SESSION_KEY); return v ? JSON.parse(v) : null; } catch(e) { return null; } };
const clearSession = ()     => { try { localStorage.removeItem(SESSION_KEY); } catch(e) {} };

/* CSV Export */
const exportCSV = (tasks) => {
  const headers = ["Task ID","Project Name","Task Name","Description","Assigned To","Priority","Status","Deadline","Task Date","Created By"];
  const rows = tasks.map(t => [
    t.taskId        || "",
    t.projectName   || "",
    t.taskName      || "",
    (t.description  || "").replace(/"/g, '""'),
    t.assignedTo    || "",
    t.priority      || "",
    t.status        || "",
    t.deadline      || "",
    t.date          || "",
    t.createdByName || "",
  ].map(v => `"${v}"`).join(","));
  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `TaskFlow_Export_${today()}.csv`;
  a.click(); URL.revokeObjectURL(url);
};

const avatarColor = (n = "") => {
  const c = ["#6366f1","#ec4899","#f59e0b","#10b981","#3b82f6","#8b5cf6","#ef4444","#14b8a6"];
  let h = 0; for (let ch of n) h = (h * 31 + ch.charCodeAt(0)) % c.length; return c[h];
};
const initials = (n = "") => n.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

const credEmail = (u, url) => ({
  subject: `Your TaskFlow Login Credentials - Welcome, ${u.name}!`,
  body: `Hello ${u.name},\n\nYour TaskFlow account has been created.\n\nLogin URL: ${url}\nEmail: ${u.email}\nPassword: ${u.password}\n\nBest regards,\nTaskFlow Admin`,
});
const credWaText = (u, url) =>
  `TaskFlow - Your Login Credentials\n\nHello ${u.name}!\n\nLogin URL: ${url}\nEmail: ${u.email}\nPassword: ${u.password}\n\nKeep these safe. - TaskFlow Admin`;

const taskShareText = (t) =>
  `TASK - ${t.taskId}\n\nProject: ${t.projectName}\nTask: ${t.taskName}\nDescription: ${t.description || "-"}\nAssigned To: ${t.assignedTo}\nPriority: ${t.priority}\nStatus: ${t.status}\nDeadline: ${fmtDate(t.deadline)}\n\n- Sent via TaskFlow`;

/* UI Atoms */
const Pill = ({ children, color, bg, border }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px",
    borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg,
    border: `1px solid ${border || bg}`, whiteSpace: "nowrap", letterSpacing: "0.04em",
  }}>{children}</span>
);

const Btn = ({ children, onClick, variant = "primary", size = "md", disabled, full, sx = {} }) => {
  const V = {
    primary:  { bg: "linear-gradient(135deg,#1e40af,#3b82f6)", color: "#fff", border: "none", shadow: "0 4px 14px rgba(37,99,235,.35)" },
    success:  { bg: "linear-gradient(135deg,#065f46,#10b981)", color: "#fff", border: "none", shadow: "0 4px 12px rgba(16,185,129,.3)" },
    danger:   { bg: "linear-gradient(135deg,#991b1b,#ef4444)", color: "#fff", border: "none", shadow: "0 4px 12px rgba(239,68,68,.3)" },
    ghost:    { bg: "transparent", color: "#374151", border: "1.5px solid #e5e7eb", shadow: "none" },
    email:    { bg: "linear-gradient(135deg,#0f766e,#14b8a6)", color: "#fff", border: "none", shadow: "0 4px 12px rgba(20,184,166,.3)" },
    whatsapp: { bg: "linear-gradient(135deg,#128C7E,#25D366)", color: "#fff", border: "none", shadow: "0 4px 12px rgba(37,211,102,.3)" },
    sms:      { bg: "linear-gradient(135deg,#1d4ed8,#60a5fa)", color: "#fff", border: "none", shadow: "0 4px 12px rgba(96,165,250,.3)" },
    copy:     { bg: "#f1f5f9", color: "#374151", border: "1.5px solid #e2e8f0", shadow: "none" },
    copydone: { bg: "#10b981", color: "#fff", border: "none", shadow: "0 4px 12px rgba(16,185,129,.3)" },
    purple:   { bg: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", border: "none", shadow: "0 4px 12px rgba(124,58,237,.3)" },
  };
  const v = V[variant] || V.primary;
  const pad = size === "sm" ? "6px 13px" : size === "lg" ? "14px 30px" : "10px 20px";
  const fs  = size === "sm" ? 12 : size === "lg" ? 15 : 14;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: v.bg, color: v.color, border: v.border || "none", boxShadow: v.shadow,
      padding: pad, borderRadius: 10, fontSize: fs, fontWeight: 600,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .6 : 1,
      display: "inline-flex", alignItems: "center", gap: 7, fontFamily: "inherit",
      width: full ? "100%" : "auto", justifyContent: full ? "center" : "flex-start", ...sx,
    }}>{children}</button>
  );
};

const Avatar = ({ name, size = 36 }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%",
    background: `linear-gradient(135deg,${avatarColor(name)},${avatarColor(name)}99)`,
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontWeight: 700, fontSize: size * .36, flexShrink: 0, userSelect: "none",
  }}>{initials(name) || "?"}</div>
);

const Field = ({ label, children }) => (
  <div>
    {label && <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</label>}
    {children}
  </div>
);

const inputCss = {
  width: "100%", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #e5e7eb",
  fontSize: 14, color: "#111", outline: "none", background: "#fafafa", fontFamily: "inherit", boxSizing: "border-box",
};

const TInput    = (p) => <Field label={p.label}><input    {...p} label={undefined} style={{ ...inputCss, ...p.style }} /></Field>;
const TTextarea = (p) => <Field label={p.label}><textarea {...p} label={undefined} style={{ ...inputCss, resize: "vertical", minHeight: 78, ...p.style }} /></Field>;
const TSelect   = ({ label, options, ...p }) => (
  <Field label={label}>
    <select {...p} style={{ ...inputCss, cursor: "pointer", ...p.style }}>
      {options.map(o => typeof o === "string"
        ? <option key={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </Field>
);

const Overlay = ({ children, onClose, maxW = 500 }) => (
  <div onClick={e => e.target === e.currentTarget && onClose()} style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 16, backdropFilter: "blur(4px)",
  }}>
    <div style={{
      background: "#fff", borderRadius: 22, width: "100%", maxWidth: maxW,
      maxHeight: "93vh", overflow: "auto", boxShadow: "0 30px 90px rgba(0,0,0,.25)", animation: "popIn .18s ease",
    }}>{children}</div>
  </div>
);

const MHead = ({ title, sub, onClose }) => (
  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
    <div>
      <h3 style={{ margin: "0 0 2px", fontSize: 18, fontWeight: 800, color: "#111" }}>{title}</h3>
      {sub && <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>{sub}</p>}
    </div>
    <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: 8, padding: "6px 8px", cursor: "pointer", flexShrink: 0 }}>
      <XIcon size={17} color="#6b7280" />
    </button>
  </div>
);

/* Icons */
const Ico = (d) => ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);
const XIcon       = Ico(<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>);
const PlusIcon    = Ico(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>);
const EditIcon    = Ico(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></>);
const TrashIcon   = Ico(<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4h6v2" /></>);
const ShareIcon   = Ico(<><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></>);
const CopyIcon    = Ico(<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>);
const MailIcon    = Ico(<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>);
const KeyIcon     = Ico(<><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></>);
const LogoutIcon  = Ico(<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>);
const TaskIcon    = Ico(<><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>);
const CalIcon     = Ico(<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>);
const EyeIcon     = Ico(<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>);
const EyeOffIcon  = Ico(<><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></>);
const UserIcon    = Ico(<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>);
const FolderIcon  = Ico(<><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></>);
const DownloadIcon= Ico(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>);
const TableIcon   = Ico(<><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></>);
const GridIcon    = Ico(<><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></>);
const ZapIcon     = Ico(<><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>);
const ChevUpIcon  = Ico(<><polyline points="18 15 12 9 6 15"/></>);
const ChevDnIcon  = Ico(<><polyline points="6 9 12 15 18 9"/></>);
const WaIcon = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" /></svg>;
const SmsIcon = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;

/* Quick Add Component */
function QuickAdd({ projects, users, currentUser, onSaved, onClose }) {
  const [lines,    setLines]    = useState("");
  const [project,  setProject]  = useState("");
  const [assignTo, setAssignTo] = useState(currentUser.role === "user" ? currentUser.name : "");
  const [deadline, setDeadline] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [saving,   setSaving]   = useState(false);

  const save = async () => {
    const tasks = lines.split("\n").map(l => l.trim()).filter(Boolean);
    if (!tasks.length) return;
    setSaving(true);
    // Create in reverse so newest (last entered) gets highest timestamp = appears at top
    for (let i = tasks.length - 1; i >= 0; i--) {
      const now = nowISO();
      const t = {
        id: `task-${uid()}`,
        taskId: `TASK-${String(Date.now()).slice(-6)}`,
        taskName: tasks[i],
        description: "",
        projectName: project,
        assignedTo: assignTo,
        priority,
        status: "Draft",
        deadline: deadline || "",
        date: today(),
        startTime: now,
        createdAt: now,
        endTime: null,
        createdBy: currentUser.id,
        createdByName: currentUser.name,
      };
      await DB.set("tasks", t.id, t);
    }
    setSaving(false);
    onSaved();
  };

  const count = lines.split("\n").filter(l => l.trim()).length;

  return (
    <div style={{ background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: 18, boxShadow: "0 8px 40px rgba(99,102,241,.13)", marginBottom: 20, overflow: "hidden" }}>
      <div style={{ background: "linear-gradient(135deg,#4f46e5,#7c3aed)", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ZapIcon size={18} color="#fff" />
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>Quick Add Tasks</span>
          <span style={{ background: "rgba(255,255,255,.2)", borderRadius: 20, padding: "2px 10px", fontSize: 11, color: "#fff", fontWeight: 600 }}>Draft mode - one task per line</span>
        </div>
        <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: 8, padding: "5px 7px", cursor: "pointer", display: "flex" }}>
          <XIcon size={15} color="#fff" />
        </button>
      </div>
      <div style={{ padding: "18px 20px" }}>
        <textarea
          autoFocus
          placeholder={"Type or paste task descriptions - one per line:\n\nReview Q4 marketing report\nSchedule kickoff meeting\nUpdate website copy\n\nAll tasks saved as Draft instantly."}
          value={lines}
          onChange={e => setLines(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) save(); }}
          style={{ ...inputCss, minHeight: 130, resize: "vertical", lineHeight: 1.7, marginBottom: 14 }}
        />
        <div className="tf-quick-grid" style={{ marginBottom:14 }}>
          {[
            { label: "Project", val: project, set: setProject, opts: [{ value: "", label: "No project" }, ...[...projects].sort((a,b) => a.name.localeCompare(b.name)).map(p => ({ value: p.name, label: p.name }))] },
            { label: "Assign To", val: assignTo, set: setAssignTo, opts: [{ value: "", label: "Unassigned" }, ...[...users].sort((a,b) => a.name.localeCompare(b.name)).map(u => ({ value: u.name, label: u.name }))] },
            { label: "Priority", val: priority, set: setPriority, opts: ["High", "Medium", "Low"].map(v => ({ value: v, label: v })) },
          ].map(f => (
            <div key={f.label}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 5, textTransform: "uppercase" }}>{f.label}</label>
              <select value={f.val} onChange={e => f.set(e.target.value)} style={{ ...inputCss, padding: "8px 12px" }}>
                {f.opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          ))}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 5, textTransform: "uppercase" }}>Deadline</label>
            <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} style={{ ...inputCss, padding: "8px 12px" }} />
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>
            {count > 0 ? <><strong style={{ color: "#4f46e5" }}>{count} task{count > 1 ? "s" : ""}</strong> ready to save as Draft</> : "Enter task names above, one per line"}
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn>
            <Btn variant="purple" size="sm" disabled={!count || saving} onClick={save}>
              <ZapIcon size={13} color="#fff" />
              {saving ? "Saving..." : `Add ${count > 1 ? count + " Tasks" : "Task"} as Draft`}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Task Table Component - Excel-style with sortable columns and per-column filters */
function TaskTable({ tasks, canEdit, onEdit, onDelete, onShare, onStatusChange, onCellChange, users, projects }) {
  const [sortCol,    setSortCol]    = useState("createdAt");
  const [sortDir,    setSortDir]    = useState("desc");
  const [colFilters, setColFilters] = useState({});
  const [selectedIds,setSelectedIds]= useState([]);
  const [editCell,   setEditCell]   = useState(null);   // { taskId, col }
  const [editVal,    setEditVal]    = useState("");
  const [bulkField,  setBulkField]  = useState("");
  const [bulkVal,    setBulkVal]    = useState("");
  const [saving,     setSaving]     = useState({});     // taskId → bool

  // ── Editable column config ─────────────────────────────────
  const EDITABLE = {
    taskName:    { type: "text"   },
    projectName: { type: "select", opts: () => projects.map(p => p.name) },
    assignedTo:  { type: "select", opts: () => users.filter(u=>u.active).map(u=>u.name) },
    priority:    { type: "select", opts: () => ["Low","Medium","High"] },
    status:      { type: "select", opts: () => ALL_STATUSES },
    deadline:    { type: "date"   },
    startTime:   { type: "datetime" },
    endTime:     { type: "datetime" },
  };

  // Bulk field options
  const BULK_FIELDS = [
    { key: "status",      label: "Status",     type: "select", opts: ALL_STATUSES },
    { key: "priority",    label: "Priority",   type: "select", opts: ["Low","Medium","High"] },
    { key: "assignedTo",  label: "Assigned To",type: "select", opts: users.filter(u=>u.active).map(u=>u.name) },
    { key: "projectName", label: "Project",    type: "select", opts: projects.map(p=>p.name) },
    { key: "deadline",    label: "Deadline",   type: "date",   opts: [] },
  ];

  const COLS = [
    { key: "_check",      label: "",            w: 38  },
    { key: "taskId",      label: "Task ID",     w: 90  },
    { key: "projectName", label: "Project",     w: 130 },
    { key: "taskName",    label: "Task Name",   w: 200 },
    { key: "assignedTo",  label: "Assigned To", w: 130 },
    { key: "priority",    label: "Priority",    w: 95  },
    { key: "status",      label: "Status",      w: 125 },
    { key: "deadline",    label: "Deadline",    w: 110 },
    { key: "startTime",   label: "Start Time",  w: 130 },
    { key: "endTime",     label: "Finish Time", w: 130 },
    { key: "duration",    label: "Duration",    w: 95  },
    { key: "createdByName",label:"Created By",  w: 115 },
  ];

  // ── Sorting / filtering ──────────────────────────────────────
  const toggleSort   = (key) => { if (sortCol===key) setSortDir(d=>d==="asc"?"desc":"asc"); else{setSortCol(key);setSortDir("asc");} };
  const setColFilter = (key,val) => setColFilters(p=>({...p,[key]:val}));
  const uniqueVals   = (key) => {
    if (["duration","startTime","endTime","_check"].includes(key)) return [];
    return [...new Set(tasks.map(t=>t[key]||"").filter(Boolean))].sort();
  };
  const getSortVal   = (t,key) => {
    if (key==="duration") return (!t.startTime||!t.endTime) ? -1 : new Date(t.endTime)-new Date(t.startTime);
    if (["startTime","endTime","createdAt"].includes(key)) return t[key]?new Date(t[key]).getTime():0;
    return (t[key]||"").toString().toLowerCase();
  };
  const filtered = tasks.filter(t=>Object.entries(colFilters).every(([k,v])=>!v||(t[k]||"")===v));
  const sorted   = [...filtered].sort((a,b)=>{
    const av=getSortVal(a,sortCol), bv=getSortVal(b,sortCol);
    if (typeof av==="number"&&typeof bv==="number") return sortDir==="asc"?av-bv:bv-av;
    return sortDir==="asc"?String(av).localeCompare(String(bv)):String(bv).localeCompare(String(av));
  });

  // ── Selection ────────────────────────────────────────────────
  const toggleOne = (id)  => setSelectedIds(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const toggleAll = ()    => setSelectedIds(p=>p.length===sorted.length?[]:sorted.map(t=>t.id));
  const clearSel  = ()    => { setSelectedIds([]); setBulkField(""); setBulkVal(""); };

  // ── Inline cell edit ─────────────────────────────────────────
  const startEdit = (t, col) => {
    if (!canEdit(t)) return;
    if (!EDITABLE[col]) return;
    setEditCell({ taskId: t.id, col });
    setEditVal(col==="startTime"||col==="endTime"
      ? (t[col] ? new Date(t[col]).toISOString().slice(0,16) : "")
      : (t[col] || ""));
  };

  const commitEdit = async (t, col, val) => {
    setEditCell(null);
    const oldVal = col==="startTime"||col==="endTime"
      ? (t[col] ? new Date(t[col]).toISOString().slice(0,16) : "")
      : (t[col]||"");
    if (val === oldVal) return;                           // no change
    setSaving(p=>({...p,[t.id]:true}));
    const update = { ...t, [col]: col==="startTime"||col==="endTime" ? (val ? new Date(val).toISOString() : null) : val };
    // Special: status change goes through updateStatus for timing logic
    if (col==="status") { await onStatusChange(t.id, val); }
    else                { await onCellChange(t.id, update); }
    setSaving(p=>({...p,[t.id]:false}));
  };

  const handleKey = (e, t, col) => {
    if (e.key==="Enter")  { e.preventDefault(); commitEdit(t,col,editVal); }
    if (e.key==="Escape") { setEditCell(null); }
  };

  // ── Bulk apply ────────────────────────────────────────────────
  const applyBulk = async () => {
    if (!bulkField||!bulkVal||!selectedIds.length) return;
    setSaving(p=>{const n={...p};selectedIds.forEach(id=>n[id]=true);return n;});
    await Promise.all(selectedIds.map(async id=>{
      const t = tasks.find(x=>x.id===id); if(!t) return;
      if (bulkField==="status") { await onStatusChange(id, bulkVal); }
      else { await onCellChange(id, {...t,[bulkField]:bulkVal}); }
    }));
    setSaving(p=>{const n={...p};selectedIds.forEach(id=>n[id]=false);return n;});
    clearSel();
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} task${selectedIds.length!==1?"s":""}? This cannot be undone.`)) return;
    await Promise.all(selectedIds.map(id=>onDelete(id)));
    clearSel();
  };

  // ── Render a single editable cell ────────────────────────────
  const renderCell = (t, col) => {
    const isEditing = editCell?.taskId===t.id && editCell?.col===col;
    const edCfg     = EDITABLE[col];
    const isSaving  = saving[t.id];
    const canE      = canEdit(t) && !!edCfg;

    const cellBase = {
      cursor: canE ? "pointer" : "default",
      padding: "4px 6px", borderRadius: 6, minWidth: 40,
      border: isEditing ? "1.5px solid #3b82f6" : "1.5px solid transparent",
      background: isEditing ? "#eff6ff" : canE ? "transparent" : "transparent",
      transition: "border .15s, background .15s",
      display: "inline-block", width: "100%", boxSizing: "border-box",
    };
    const hoverStyle = canE ? { onMouseEnter: e=>{ if(!isEditing) e.currentTarget.style.border="1.5px solid #d1d5db"; e.currentTarget.style.background="#f8fafc"; }, onMouseLeave: e=>{ if(!isEditing) e.currentTarget.style.border="1.5px solid transparent"; e.currentTarget.style.background="transparent"; } } : {};

    if (isEditing && edCfg) {
      const inpStyle = { width:"100%", padding:"3px 6px", borderRadius:5, border:"none", fontSize:12, outline:"none", fontFamily:"inherit", background:"transparent" };
      if (edCfg.type==="select") {
        return (
          <select autoFocus value={editVal} onChange={e=>setEditVal(e.target.value)}
            onBlur={()=>commitEdit(t,col,editVal)} onKeyDown={e=>handleKey(e,t,col)}
            style={{...inpStyle,cursor:"pointer"}}>
            <option value="">— none —</option>
            {edCfg.opts().map(o=><option key={o} value={o}>{o}</option>)}
          </select>
        );
      }
      if (edCfg.type==="date") {
        return <input autoFocus type="date" value={editVal} onChange={e=>setEditVal(e.target.value)}
          onBlur={()=>commitEdit(t,col,editVal)} onKeyDown={e=>handleKey(e,t,col)} style={inpStyle}/>;
      }
      if (edCfg.type==="datetime") {
        return <input autoFocus type="datetime-local" value={editVal} onChange={e=>setEditVal(e.target.value)}
          onBlur={()=>commitEdit(t,col,editVal)} onKeyDown={e=>handleKey(e,t,col)} style={inpStyle}/>;
      }
      return <input autoFocus type="text" value={editVal} onChange={e=>setEditVal(e.target.value)}
        onBlur={()=>commitEdit(t,col,editVal)} onKeyDown={e=>handleKey(e,t,col)} style={inpStyle}/>;
    }

    // Display mode — render value with edit hint
    const pc = PRIORITY_CFG[t.priority]||PRIORITY_CFG.Medium;
    const sc = STATUS_CFG[t.status]    ||STATUS_CFG["Draft"];
    const over = isOver(t);

    let content;
    switch(col) {
      case "taskId":
        content = <span style={{fontSize:11,color:"#9ca3af",fontWeight:700}}>{t.taskId}</span>;
        break;
      case "projectName":
        content = <span style={{fontSize:12,color:"#2563eb",fontWeight:600}}>{t.projectName||"—"}</span>;
        break;
      case "taskName":
        content = (
          <div>
            <span style={{fontWeight:600,color:"#111"}}>{t.taskName}</span>
            {t.description&&<div style={{fontSize:11,color:"#9ca3af",marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:175}}>{t.description}</div>}
          </div>
        );
        break;
      case "assignedTo":
        content = (
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <Avatar name={t.assignedTo||"?"} size={20}/>
            <span style={{fontSize:12}}>{t.assignedTo||"—"}</span>
          </div>
        );
        break;
      case "priority":
        content = <Pill color={pc.color} bg={pc.bg} border={pc.border}>● {t.priority}</Pill>;
        break;
      case "status":
        content = (
          <span style={{padding:"3px 9px",borderRadius:7,border:`1.5px solid ${sc.border}`,background:sc.bg,color:sc.color,fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>
            {t.status}
          </span>
        );
        break;
      case "deadline":
        content = <span style={{color:over?"#dc2626":"#374151",fontWeight:over?700:400}}>{fmtDate(t.deadline)||"—"}{over&&" ⚠"}</span>;
        break;
      case "startTime":
        content = <span style={{fontSize:11,color:"#374151"}}>{fmtTime(t.startTime)||"—"}</span>;
        break;
      case "endTime":
        content = t.endTime
          ? <span style={{fontSize:11,color:"#059669",fontWeight:600}}>{fmtTime(t.endTime)}</span>
          : <span style={{fontSize:11,color:"#d1d5db"}}>—</span>;
        break;
      case "duration":
        content = t.startTime&&t.endTime
          ? <span style={{fontSize:11,fontWeight:700,color:"#7c3aed",background:"#f5f3ff",padding:"2px 8px",borderRadius:6}}>{fmtDuration(t.startTime,t.endTime,t.pausedDuration||0)}</span>
          : t.status==="Hold"
            ? <span style={{fontSize:11,fontWeight:700,color:"#b45309",background:"#fffbeb",padding:"2px 8px",borderRadius:6}}>⏸ Hold</span>
            : <span style={{fontSize:11,color:"#d1d5db"}}>—</span>;
        break;
      case "createdByName":
        content = (
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <Avatar name={t.createdByName||"?"} size={20}/>
            <span style={{fontSize:12}}>{t.createdByName||"—"}</span>
          </div>
        );
        break;
      default: content = <span>{t[col]||"—"}</span>;
    }

    return (
      <div style={{...cellBase, position:"relative"}} onClick={()=>startEdit(t,col)} {...hoverStyle}>
        {content}
        {canE && !isEditing && (
          <span style={{position:"absolute",top:2,right:3,fontSize:9,color:"#d1d5db",opacity:0,transition:"opacity .15s"}}
            className="edit-hint">✏</span>
        )}
        {isSaving && <span style={{position:"absolute",top:2,right:3,fontSize:9,color:"#3b82f6"}}>↻</span>}
      </div>
    );
  };

  // ── Styles ────────────────────────────────────────────────────
  const thS = { padding:"10px 10px", textAlign:"left", fontWeight:700, fontSize:11, color:"#374151", background:"#f8fafc", borderBottom:"2px solid #e5e7eb", whiteSpace:"nowrap", cursor:"pointer", userSelect:"none", borderRight:"1px solid #e5e7eb", position:"sticky", top:0, zIndex:2 };
  const tdS = { padding:"6px 6px", fontSize:12, color:"#374151", borderBottom:"1px solid #f0f0f0", borderRight:"1px solid #f5f5f5", verticalAlign:"middle", whiteSpace:"nowrap" };

  if (tasks.length===0) return (
    <div style={{background:"#fff",borderRadius:16,padding:"56px 24px",textAlign:"center",border:"1.5px dashed #e5e7eb"}}>
      <div style={{fontSize:48,marginBottom:12}}>📋</div>
      <h3 style={{margin:"0 0 6px",color:"#374151",fontWeight:700}}>No tasks found</h3>
    </div>
  );

  const hasFilters = Object.values(colFilters).some(Boolean);
  const selCount   = selectedIds.length;
  const bulkFldCfg = BULK_FIELDS.find(f=>f.key===bulkField);

  return (
    <div style={{background:"#fff",borderRadius:16,border:"1.5px solid #e5e7eb",overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,.05)"}}>

      {/* ── Bulk action bar ─────────────────────────────────── */}
      {selCount>0 && (
        <div style={{padding:"9px 16px",background:"linear-gradient(90deg,#1e3a8a,#4f46e5)",display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:13,fontWeight:700,color:"#fff",flexShrink:0}}>✓ {selCount} selected</span>

          {/* Field picker */}
          <select value={bulkField} onChange={e=>{setBulkField(e.target.value);setBulkVal("");}}
            style={{fontSize:12,padding:"5px 10px",borderRadius:7,border:"1.5px solid rgba(255,255,255,.35)",background:"rgba(255,255,255,.15)",color:"#fff",fontWeight:600,cursor:"pointer",outline:"none",fontFamily:"inherit"}}>
            <option value="" style={{color:"#111"}}>Update field…</option>
            {BULK_FIELDS.map(f=><option key={f.key} value={f.key} style={{color:"#111"}}>{f.label}</option>)}
          </select>

          {/* Value picker */}
          {bulkFldCfg && (
            bulkFldCfg.type==="date"
              ? <input type="date" value={bulkVal} onChange={e=>setBulkVal(e.target.value)}
                  style={{fontSize:12,padding:"5px 10px",borderRadius:7,border:"1.5px solid rgba(255,255,255,.35)",background:"rgba(255,255,255,.15)",color:"#fff",outline:"none",fontFamily:"inherit"}}/>
              : <select value={bulkVal} onChange={e=>setBulkVal(e.target.value)}
                  style={{fontSize:12,padding:"5px 10px",borderRadius:7,border:"1.5px solid rgba(255,255,255,.35)",background:"rgba(255,255,255,.15)",color:"#fff",fontWeight:600,cursor:"pointer",outline:"none",fontFamily:"inherit"}}>
                  <option value="" style={{color:"#111"}}>Pick value…</option>
                  {bulkFldCfg.opts.map(o=><option key={o} value={o} style={{color:"#111"}}>{o}</option>)}
                </select>
          )}

          <button onClick={applyBulk} disabled={!bulkField||!bulkVal}
            style={{fontSize:12,fontWeight:700,padding:"5px 14px",borderRadius:7,border:"none",background:bulkField&&bulkVal?"#22c55e":"rgba(255,255,255,.2)",color:"#fff",cursor:bulkField&&bulkVal?"pointer":"not-allowed",fontFamily:"inherit"}}>
            Apply
          </button>

          <div style={{width:1,height:20,background:"rgba(255,255,255,.25)",margin:"0 2px"}}/>

          <button onClick={bulkDelete}
            style={{fontSize:12,fontWeight:700,padding:"5px 14px",borderRadius:7,border:"none",background:"#ef4444",color:"#fff",cursor:"pointer",fontFamily:"inherit"}}>
            🗑 Delete
          </button>

          <button onClick={clearSel}
            style={{fontSize:12,fontWeight:600,padding:"5px 10px",borderRadius:7,border:"1.5px solid rgba(255,255,255,.3)",background:"transparent",color:"#cbd5e1",cursor:"pointer",marginLeft:"auto",fontFamily:"inherit"}}>
            ✕ Clear
          </button>
        </div>
      )}

      {/* ── Filter info bar ───────────────────────────────────── */}
      {hasFilters && (
        <div style={{padding:"8px 16px",background:"#eff6ff",borderBottom:"1px solid #bfdbfe",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:12,color:"#1e40af",fontWeight:600}}>
            Showing {sorted.length} of {tasks.length} tasks (filters active)
          </span>
          <button onClick={()=>setColFilters({})} style={{fontSize:11,color:"#1e40af",background:"none",border:"none",cursor:"pointer",fontWeight:700,textDecoration:"underline"}}>
            Clear all filters
          </button>
        </div>
      )}

      {/* ── Inline edit hint bar ─────────────────────────────── */}
      <div style={{padding:"5px 14px",background:"#f8fafc",borderBottom:"1px solid #f0f0f0",fontSize:11,color:"#9ca3af",display:"flex",alignItems:"center",gap:6}}>
        <span>✏️</span>
        <span>Click any cell to edit inline · Press Enter to save · Esc to cancel · Select rows for bulk updates</span>
      </div>

      <div style={{overflowX:"auto"}}>
        <style>{`.edit-hint{opacity:0!important} tr:hover .edit-hint{opacity:1!important}`}</style>
        <table style={{width:"100%",borderCollapse:"collapse",tableLayout:"fixed"}}>
          <colgroup>
            {COLS.map(c=><col key={c.key} style={{width:c.w}}/>)}
            <col style={{width:90}}/>
          </colgroup>
          <thead>
            <tr>
              {COLS.map(c=>c.key==="_check"
                ? <th key="_check" style={{...thS,cursor:"default",textAlign:"center",padding:"10px 8px"}}>
                    <input type="checkbox" checked={selCount===sorted.length&&sorted.length>0}
                      onChange={toggleAll} style={{width:14,height:14,cursor:"pointer",accentColor:"#4f46e5"}}
                      title={selCount===sorted.length?"Deselect all":"Select all"}/>
                  </th>
                : <th key={c.key} style={thS} onClick={()=>toggleSort(c.key)}>
                    <div style={{display:"flex",alignItems:"center",gap:4}}>
                      {c.label}
                      {sortCol===c.key
                        ?(sortDir==="asc"?<ChevUpIcon size={12} color="#2563eb"/>:<ChevDnIcon size={12} color="#2563eb"/>)
                        :<ChevDnIcon size={12} color="#d1d5db"/>}
                    </div>
                  </th>
              )}
              <th style={{...thS,cursor:"default"}}>Actions</th>
            </tr>
            <tr style={{background:"#f1f5f9"}}>
              {COLS.map(c=>c.key==="_check"
                ? <th key="_check" style={{padding:"5px 8px",borderBottom:"2px solid #e5e7eb",borderRight:"1px solid #e5e7eb"}}/>
                : <th key={c.key} style={{padding:"5px 8px",borderBottom:"2px solid #e5e7eb",borderRight:"1px solid #e5e7eb"}}>
                    <select value={colFilters[c.key]||""} onChange={e=>setColFilter(c.key,e.target.value)}
                      style={{width:"100%",fontSize:11,padding:"3px 5px",borderRadius:5,border:"1px solid #d1d5db",background:"#fff",cursor:"pointer",color:colFilters[c.key]?"#1e40af":"#9ca3af",fontWeight:colFilters[c.key]?700:400}}>
                      <option value="">All</option>
                      {uniqueVals(c.key).map(v=><option key={v} value={v}>{v}</option>)}
                    </select>
                  </th>
              )}
              <th style={{padding:"5px 8px",borderBottom:"2px solid #e5e7eb"}}/>
            </tr>
          </thead>
          <tbody>
            {sorted.map((t,i)=>{
              const isSel = selectedIds.includes(t.id);
              return (
                <tr key={t.id}
                  style={{background:isSel?"#eef2ff":i%2===0?"#fff":"#fafafa",outline:isSel?"2px solid #818cf8 inset":"none",transition:"background .1s"}}
                  onMouseEnter={e=>{if(!isSel)e.currentTarget.style.background="#f0f9ff";}}
                  onMouseLeave={e=>{if(!isSel)e.currentTarget.style.background=i%2===0?"#fff":"#fafafa";}}>

                  {/* Checkbox */}
                  <td style={{...tdS,textAlign:"center",padding:"6px 8px"}}>
                    <input type="checkbox" checked={isSel} onChange={()=>toggleOne(t.id)}
                      style={{width:14,height:14,cursor:"pointer",accentColor:"#4f46e5"}}/>
                  </td>

                  {/* Data cells */}
                  {["taskId","projectName","taskName","assignedTo","priority","status","deadline","startTime","endTime","duration","createdByName"].map(col=>(
                    <td key={col} style={{
                      ...tdS,
                      whiteSpace: col==="taskName"?"normal":"nowrap",
                      maxWidth:   col==="taskName"?200:undefined,
                      padding:    "4px 6px",
                    }}>
                      {renderCell(t,col)}
                    </td>
                  ))}

                  {/* Actions */}
                  <td style={{...tdS,borderRight:"none",padding:"6px 8px"}}>
                    <div style={{display:"flex",gap:4}}>
                      <button onClick={()=>onShare(t)} title="Share" style={{background:"#f0fdf4",border:"none",borderRadius:6,padding:"5px 6px",cursor:"pointer"}}><ShareIcon size={13} color="#10b981"/></button>
                      {canEdit(t)&&<>
                        <button onClick={()=>onEdit(t)} title="Full Edit" style={{background:"#eff6ff",border:"none",borderRadius:6,padding:"5px 6px",cursor:"pointer"}}><EditIcon size={13} color="#2563eb"/></button>
                        <button onClick={()=>onDelete(t.id)} title="Delete" style={{background:"#fef2f2",border:"none",borderRadius:6,padding:"5px 6px",cursor:"pointer"}}><TrashIcon size={13} color="#dc2626"/></button>
                      </>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{padding:"10px 16px",borderTop:"1px solid #f0f0f0",background:"#fafafa",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span style={{fontSize:12,color:"#9ca3af"}}>
          {sorted.length} task{sorted.length!==1?"s":""} shown{sorted.length!==tasks.length?` (${tasks.length} total)`:""}{selCount>0&&<span style={{marginLeft:8,color:"#4f46e5",fontWeight:700}}>· {selCount} selected</span>}
        </span>
        <span style={{fontSize:11,color:"#d1d5db"}}>Click any cell to edit · headers to sort · dropdowns to filter</span>
      </div>
    </div>
  );
}

/* Project Management */
function ProjectManagement({ projects, onProjectsChange }) {
  const [name,    setName]    = useState("");
  const [err,     setErr]     = useState("");
  const [delConf, setDelConf] = useState(null);

  const add = async () => {
    const trimmed = name.trim();
    setErr("");
    if (!trimmed) return setErr("Project name cannot be empty.");
    if (projects.find(p => p.name.toLowerCase() === trimmed.toLowerCase())) return setErr("A project with this name already exists.");
    const proj = { id: `proj-${uid()}`, name: trimmed, createdAt: today() };
    await DB.set("projects", proj.id, proj);
    setName("");
  };

  const remove = async (proj) => {
    await DB.del("projects", proj.id);
    setDelConf(null);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: "0 0 3px", fontSize: 22, fontWeight: 800, color: "#0f172a" }}>Projects</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#9ca3af" }}>{projects.length} project{projects.length !== 1 ? "s" : ""} - Admin-managed - stored in Firebase</p>
        </div>
      </div>
      <div style={{ background: "linear-gradient(135deg,#eff6ff,#faf5ff)", border: "1.5px solid #bfdbfe", borderRadius: 14, padding: "14px 18px", marginBottom: 22, display: "flex", gap: 14, alignItems: "flex-start" }}>
        <span style={{ fontSize: 24, flexShrink: 0 }}>📁</span>
        <div>
          <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 13, color: "#1e40af" }}>Admin-Controlled Project Names</p>
          <p style={{ margin: 0, fontSize: 12, color: "#374151", lineHeight: 1.7 }}>Only admins can create or delete projects. Team members select from this list when creating tasks.</p>
        </div>
      </div>
      <div style={{ background: "#fff", borderRadius: 16, padding: "20px 22px", border: "1.5px solid #f0f0f0", marginBottom: 20 }}>
        <p style={{ margin: "0 0 12px", fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Add New Project</p>
        <div style={{ display: "flex", gap: 10 }}>
          <input placeholder="e.g. Q4 Marketing Campaign" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} style={{ ...inputCss, flex: 1 }} />
          <Btn onClick={add} variant="primary" disabled={!name.trim()}><PlusIcon size={14} color="#fff" /> Add Project</Btn>
        </div>
        {err && <div style={{ marginTop: 10, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#dc2626" }}>⚠ {err}</div>}
      </div>
      {projects.length === 0 ? (
        <div style={{ textAlign: "center", padding: "52px 24px", background: "#fff", borderRadius: 16, border: "1.5px dashed #e5e7eb" }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>📁</div>
          <h3 style={{ margin: "0 0 6px", color: "#374151", fontWeight: 700 }}>No projects yet</h3>
          <p style={{ margin: 0, color: "#9ca3af", fontSize: 14 }}>Add your first project above</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {projects.map(p => (
            <div key={p.id} style={{ background: "#fff", borderRadius: 14, padding: "14px 18px", border: "1.5px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#eff6ff,#ddd6fe)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <FolderIcon size={16} color="#7c3aed" />
                </div>
                <div>
                  <p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: 14, color: "#111" }}>{p.name}</p>
                  <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>Added {fmtDate(p.createdAt)}</p>
                </div>
              </div>
              {delConf === p.id ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 600 }}>Delete "{p.name}"?</span>
                  <Btn size="sm" variant="danger" onClick={() => remove(p)}>Yes, Delete</Btn>
                  <Btn size="sm" variant="ghost" onClick={() => setDelConf(null)}>Cancel</Btn>
                </div>
              ) : (
                <Btn size="sm" variant="danger" onClick={() => setDelConf(p.id)}><TrashIcon size={13} color="#fff" /> Delete</Btn>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Credential Share Modal */
function CredShareModal({ user, appUrl, onClose }) {
  const [copied, setCopied] = useState(false);
  const [tab,    setTab]    = useState("email");
  const eData  = credEmail(user, appUrl);
  const waText = credWaText(user, appUrl);
  const mailtoHref = `mailto:${user.email}?subject=${encodeURIComponent(eData.subject)}&body=${encodeURIComponent(eData.body)}`;
  const waHref     = `https://wa.me/?text=${encodeURIComponent(waText)}`;
  const smsHref    = `sms:?body=${encodeURIComponent(waText)}`;
  const copy = (txt) => navigator.clipboard?.writeText(txt).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2200); });
  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)} style={{ padding: "7px 16px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: tab === key ? "#fff" : "transparent", color: tab === key ? "#1e40af" : "#6b7280", boxShadow: tab === key ? "0 1px 4px rgba(0,0,0,.1)" : "none" }}>{label}</button>
  );
  return (
    <Overlay onClose={onClose} maxW={520}>
      <div style={{ padding: "28px 28px 26px" }}>
        <MHead title="Share Login Credentials" sub={`Sending credentials to ${user.name}`} onClose={onClose} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#f8fafc", borderRadius: 12, padding: "12px 16px", marginBottom: 20, border: "1.5px solid #e2e8f0" }}>
          <Avatar name={user.name} size={42} />
          <div>
            <p style={{ margin: "0 0 2px", fontWeight: 700, fontSize: 15, color: "#111" }}>{user.name}</p>
            <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>📧 {user.email} · 🔑 {user.password}</p>
          </div>
        </div>
        <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3, gap: 2, marginBottom: 18 }}>
          {tabBtn("email", "📧 Email")}
          {tabBtn("message", "💬 WhatsApp / SMS")}
        </div>
        {tab === "email" && (
          <div>
            <pre style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "13px 15px", fontSize: 12, color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.75, fontFamily: "monospace", maxHeight: 170, overflow: "auto", marginBottom: 14 }}>{eData.body}</pre>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <a href={mailtoHref} style={{ textDecoration: "none" }}><Btn variant="email" full sx={{ justifyContent: "center" }}><MailIcon size={16} color="#fff" /> Open in Email App</Btn></a>
              <Btn variant={copied ? "copydone" : "copy"} full onClick={() => copy(eData.body)} sx={{ justifyContent: "center" }}><CopyIcon size={14} color={copied ? "#fff" : "#374151"} /> {copied ? "Copied!" : "Copy Email Body"}</Btn>
            </div>
          </div>
        )}
        {tab === "message" && (
          <div>
            <pre style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "13px 15px", fontSize: 12, color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.75, fontFamily: "monospace", maxHeight: 170, overflow: "auto", marginBottom: 14 }}>{waText}</pre>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              <a href={waHref} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}><Btn variant="whatsapp" full sx={{ justifyContent: "center" }}><WaIcon /> Send via WhatsApp</Btn></a>
              <a href={smsHref} style={{ textDecoration: "none" }}><Btn variant="sms" full sx={{ justifyContent: "center" }}><SmsIcon /> Send via SMS</Btn></a>
              <Btn variant={copied ? "copydone" : "copy"} full onClick={() => copy(waText)} sx={{ justifyContent: "center" }}><CopyIcon size={14} color={copied ? "#fff" : "#374151"} /> {copied ? "Copied!" : "Copy Message"}</Btn>
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
}

/* Task Share Modal */
/* Multi-Task Share Modal - share one or many tasks at once */
function TaskShareModal({ task, allTasks, onClose }) {
  const isMultiTrigger = task === "multi";
  const [copied,   setCopied]   = useState(false);
  const [selected, setSelected] = useState(!isMultiTrigger && task ? [task.id] : []);
  const [mode,     setMode]     = useState(isMultiTrigger ? "multi" : "single");

  const tasksToShare = (mode === "single" && task && !isMultiTrigger)
    ? [task]
    : allTasks.filter(t => selected.includes(t.id));

  const text = tasksToShare.map(t =>
    `TASK - ${t.taskId}\nProject: ${t.projectName || "-"}\nTask: ${t.taskName}\nDescription: ${t.description || "-"}\nAssigned To: ${t.assignedTo || "-"}\nPriority: ${t.priority}\nStatus: ${t.status}\nDeadline: ${fmtDate(t.deadline)}`
  ).join("\n\n----------------------------\n\n") + "\n\n- Sent via TaskFlow";

  const copy = () => navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });

  const toggleSelect = (id) => setSelected(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  return (
    <Overlay onClose={onClose} maxW={520}>
      <div style={{ padding: "28px 28px 26px" }}>
        <MHead title="Share Tasks" sub={mode === "single" && task ? task.taskName : `${tasksToShare.length} task${tasksToShare.length !== 1 ? "s" : ""} selected`} onClose={onClose} />

        {/* Mode toggle */}
        <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3, gap: 2, marginBottom: 16 }}>
          {[["single","Share One Task"],["multi","Share Multiple Tasks"]].map(([k,l]) => (
            <button key={k} onClick={() => setMode(k)} style={{ flex: 1, padding: "7px 12px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: mode === k ? "#fff" : "transparent", color: mode === k ? "#1e40af" : "#6b7280", boxShadow: mode === k ? "0 1px 4px rgba(0,0,0,.1)" : "none" }}>{l}</button>
          ))}
        </div>

        {/* Multi-select task list */}
        {mode === "multi" && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Select tasks to share:</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setSelected(allTasks.map(t => t.id))} style={{ fontSize: 11, color: "#2563eb", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Select All</button>
                <button onClick={() => setSelected([])} style={{ fontSize: 11, color: "#9ca3af", background: "none", border: "none", cursor: "pointer" }}>Clear</button>
              </div>
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto", border: "1.5px solid #e5e7eb", borderRadius: 10, background: "#fafafa" }}>
              {allTasks.map(t => (
                <label key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: "1px solid #f0f0f0", cursor: "pointer", background: selected.includes(t.id) ? "#eff6ff" : "transparent" }}>
                  <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)} style={{ width: 15, height: 15, cursor: "pointer" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.taskName}</p>
                    <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{t.taskId} · {t.assignedTo || "Unassigned"} · <span style={{ color: STATUS_CFG[t.status]?.color || "#6b7280" }}>{t.status}</span></p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Preview */}
        {tasksToShare.length > 0 && (
          <pre style={{ background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 12, padding: "14px 16px", fontSize: 11.5, color: "#374151", whiteSpace: "pre-wrap", lineHeight: 1.7, fontFamily: "monospace", maxHeight: 180, overflow: "auto", marginBottom: 16 }}>{text}</pre>
        )}

        {tasksToShare.length === 0 ? (
          <p style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, padding: "12px 0" }}>Select at least one task to share</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}><Btn variant="whatsapp" full sx={{ justifyContent: "center" }}><WaIcon /> Share via WhatsApp ({tasksToShare.length})</Btn></a>
            <a href={`sms:?body=${encodeURIComponent(text)}`} style={{ textDecoration: "none" }}><Btn variant="sms" full sx={{ justifyContent: "center" }}><SmsIcon /> Share via SMS ({tasksToShare.length})</Btn></a>
            <Btn variant={copied ? "copydone" : "copy"} full onClick={copy} sx={{ justifyContent: "center" }}><CopyIcon size={15} color={copied ? "#fff" : "#374151"} /> {copied ? "Copied!" : `Copy ${tasksToShare.length > 1 ? tasksToShare.length + " Tasks" : "Task"}`}</Btn>
          </div>
        )}
      </div>
    </Overlay>
  );
}

/* Create User Modal */
function CreateUserModal({ users, appUrl, onCreated, onClose }) {
  const [f,        setF]        = useState({ name: "", email: "", password: "", role: "user" });
  const [showPass, setShowPass] = useState(false);
  const [err,      setErr]      = useState("");
  const [saving,   setSaving]   = useState(false);
  const [created,  setCreated]  = useState(null);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const genPwd = () => {
    const c = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#!";
    let p = ""; for (let i = 0; i < 10; i++) p += c[Math.floor(Math.random() * c.length)];
    set("password", p);
  };
  const submit = async () => {
    setErr("");
    if (!f.name.trim())  return setErr("Full name is required.");
    if (!f.email.trim()) return setErr("Email is required.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) return setErr("Enter a valid email address.");
    if (users.find(u => u.email === f.email.toLowerCase())) return setErr("This email is already registered.");
    if (f.password.length < 6) return setErr("Password must be at least 6 characters.");
    setSaving(true);
    const newUser = { id: `user-${uid()}`, name: f.name.trim(), email: f.email.trim().toLowerCase(), password: f.password, role: f.role, active: true, createdAt: today() };
    await DB.set("users", newUser.id, newUser);
    setSaving(false);
    setCreated(newUser);
  };
  if (created) {
    const eData  = credEmail(created, appUrl);
    const waText = credWaText(created, appUrl);
    return (
      <Overlay onClose={onClose} maxW={500}>
        <div style={{ padding:"24px 24px 22px" }}>
          <div style={{ background:"linear-gradient(135deg,#065f46,#10b981)", borderRadius:14, padding:"16px 20px", marginBottom:22, display:"flex", alignItems:"center", gap:14 }}>
            <span style={{ fontSize:32 }}>✅</span>
            <div>
              <p style={{ margin:"0 0 3px", fontWeight:700, fontSize:16, color:"#fff" }}>Account Created!</p>
              <p style={{ margin:0, fontSize:12, color:"rgba(255,255,255,.8)" }}>{created.name} ({created.role}) — can log in from any device</p>
            </div>
          </div>
          <div style={{ background:"#f8fafc", borderRadius:12, padding:"12px 16px", marginBottom:18, border:"1.5px solid #e2e8f0" }}>
            <p style={{ margin:"0 0 4px", fontWeight:700, fontSize:14 }}>{created.name}</p>
            <p style={{ margin:0, fontSize:12, color:"#6b7280" }}>📧 {created.email} · 🔑 {created.password} · Role: {created.role}</p>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
            <a href={`mailto:${created.email}?subject=${encodeURIComponent(eData.subject)}&body=${encodeURIComponent(eData.body)}`} style={{ textDecoration:"none" }}><Btn variant="email" full sx={{ justifyContent:"center" }}><MailIcon size={15} color="#fff" /> Send via Email</Btn></a>
            <a href={`https://wa.me/?text=${encodeURIComponent(waText)}`} target="_blank" rel="noreferrer" style={{ textDecoration:"none" }}><Btn variant="whatsapp" full sx={{ justifyContent:"center" }}><WaIcon /> Send via WhatsApp</Btn></a>
          </div>
          <div style={{ marginTop:18, display:"flex", justifyContent:"flex-end" }}><Btn variant="ghost" onClick={onClose}>Done</Btn></div>
        </div>
      </Overlay>
    );
  }
  return (
    <Overlay onClose={onClose} maxW={480}>
      <div style={{ padding:"24px 24px 22px" }}>
        <MHead title="Add New Member" sub="Saved to Firebase - accessible from any device" onClose={onClose} />
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <TInput label="Full Name *" placeholder="e.g. Alice Johnson" value={f.name} onChange={e => set("name", e.target.value)} />
          <TInput label="Email Address *" type="email" placeholder="alice@company.com" value={f.email} onChange={e => set("email", e.target.value)} />
          <Field label="Password *">
            <div style={{ position:"relative" }}>
              <input type={showPass ? "text" : "password"} placeholder="Min. 6 characters" value={f.password} onChange={e => set("password", e.target.value)} style={{ ...inputCss, paddingRight:84 }} />
              <div style={{ position:"absolute", right:6, top:"50%", transform:"translateY(-50%)", display:"flex", gap:4 }}>
                <button type="button" onClick={() => setShowPass(p => !p)} style={{ background:"#f1f5f9", border:"none", borderRadius:6, padding:"4px 6px", cursor:"pointer", display:"flex" }}>
                  {showPass ? <EyeOffIcon size={13} color="#6b7280" /> : <EyeIcon size={13} color="#6b7280" />}
                </button>
                <button type="button" onClick={genPwd} style={{ background:"#eff6ff", border:"none", borderRadius:6, padding:"4px 7px", cursor:"pointer", fontSize:10, fontWeight:700, color:"#2563eb" }}>AUTO</button>
              </div>
            </div>
          </Field>
          {/* Role selector */}
          <Field label="Role *">
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[["user","👤 User","Standard team member — can view and manage assigned tasks"],["admin","🔐 Admin","Full access — manage users, projects, and all tasks"]].map(([val, label, desc]) => (
                <label key={val} style={{ display:"flex", flexDirection:"column", gap:4, padding:"12px 14px", borderRadius:12, border:`2px solid ${f.role===val?"#3b82f6":"#e5e7eb"}`, background:f.role===val?"#eff6ff":"#fafafa", cursor:"pointer", transition:"all .15s" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <input type="radio" name="role" value={val} checked={f.role===val} onChange={() => set("role", val)} style={{ width:15, height:15, accentColor:"#3b82f6" }} />
                    <span style={{ fontWeight:700, fontSize:13, color:f.role===val?"#1e40af":"#374151" }}>{label}</span>
                  </div>
                  <p style={{ margin:0, fontSize:11, color:"#9ca3af", lineHeight:1.4, paddingLeft:23 }}>{desc}</p>
                </label>
              ))}
            </div>
          </Field>
        </div>
        {err && <div style={{ background:"#fef2f2", border:"1.5px solid #fecaca", borderRadius:10, padding:"10px 13px", color:"#dc2626", fontSize:13, marginTop:14 }}>⚠ {err}</div>}
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:18, flexWrap:"wrap" }}>
          <Btn onClick={onClose} variant="ghost">Cancel</Btn>
          <Btn onClick={submit} variant="primary" disabled={!f.name || !f.email || !f.password || saving}><UserIcon size={14} color="#fff" /> {saving ? "Saving..." : "Create Member"}</Btn>
        </div>
      </div>
    </Overlay>
  );
}

/* User Management Panel */
function UserManagement({ users, tasks, appUrl, onUsersChange }) {
  const [showCreate,   setShowCreate]   = useState(false);
  const [resendUser,   setResendUser]   = useState(null);
  const [roleConfirm,  setRoleConfirm]  = useState(null);
  const [logoutConfirm,setLogoutConfirm]= useState(null);

  const toggleActive = async (u) => {
    await DB.set("users", u.id, { ...u, active: !u.active });
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete "${u.name}"?`)) return;
    await DB.del("users", u.id);
  };
  const changeRole = async (u, newRole) => {
    await DB.set("users", u.id, { ...u, role: newRole });
    setRoleConfirm(null);
  };
  const forceLogoutUser = async (u) => {
    await DB.set("users", u.id, { ...u, forceLogout: true });
    setLogoutConfirm(null);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: "0 0 3px", fontSize: 22, fontWeight: 800, color: "#0f172a" }}>Team Members</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#9ca3af" }}>{users.length} member{users.length !== 1 ? "s" : ""} - stored in Firebase</p>
        </div>
        <Btn onClick={() => setShowCreate(true)} variant="primary"><PlusIcon size={15} color="#fff" /> Add Member</Btn>
      </div>
      {users.length === 0 && (
        <div style={{ textAlign: "center", padding: "56px 24px", background: "#fff", borderRadius: 16, border: "1.5px dashed #e5e7eb" }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>👥</div>
          <h3 style={{ margin: "0 0 6px", color: "#374151", fontWeight: 700 }}>No team members yet</h3>
          <Btn onClick={() => setShowCreate(true)} variant="primary"><PlusIcon size={14} color="#fff" /> Add First Member</Btn>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {users.map(u => {
          const ut   = tasks.filter(t => t.assignedTo === u.name);
          const done = ut.filter(t => t.status === "Completed").length;
          const inp  = ut.filter(t => t.status === "In Progress").length;
          const isAdminRole = u.role === "admin";
          return (
            <div key={u.id} className="tf-user-row" style={{ background:"#fff", borderRadius:16, padding:"18px 20px", border:"1.5px solid #f0f0f0", boxShadow:"0 2px 8px rgba(0,0,0,.04)" }}>
              <Avatar name={u.name} size={48} />
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "#111" }}>{u.name}</p>
                  <Pill color={u.active ? "#059669" : "#6b7280"} bg={u.active ? "#f0fdf4" : "#f9fafb"} border={u.active ? "#a7f3d0" : "#e5e7eb"}>{u.active ? "Active" : "Inactive"}</Pill>
                  {/* Role badge */}
                  <Pill
                    color={isAdminRole ? "#92400e" : "#1e40af"}
                    bg={isAdminRole ? "#fef3c7" : "#eff6ff"}
                    border={isAdminRole ? "#fde68a" : "#bfdbfe"}>
                    {isAdminRole ? "🔐 Admin" : "👤 User"}
                  </Pill>
                </div>
                <p style={{ margin: "0 0 3px", fontSize: 12, color: "#6b7280" }}>📧 {u.email}</p>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <KeyIcon size={11} color="#9ca3af" />
                  <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "monospace" }}>{u.password}</span>
                </div>
              </div>
              <div className="tf-user-stats">
                {[{ l: "Tasks", v: ut.length, c: "#3b82f6" }, { l: "Active", v: inp, c: "#7c3aed" }, { l: "Done", v: done, c: "#059669" }].map(s => (
                  <div key={s.l} style={{ textAlign: "center" }}>
                    <p style={{ margin: "0 0 1px", fontWeight: 800, fontSize: 20, color: s.c }}>{s.v}</p>
                    <p style={{ margin: 0, fontSize: 10, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase" }}>{s.l}</p>
                  </div>
                ))}
              </div>
              <div className="tf-user-actions">
                <Btn size="sm" variant="email" onClick={() => setResendUser(u)}><MailIcon size={13} color="#fff" /> Send Creds</Btn>
                <Btn size="sm" variant={u.active ? "ghost" : "success"} onClick={() => toggleActive(u)}>{u.active ? "Deactivate" : "Activate"}</Btn>
                {/* Role toggle button */}
                {roleConfirm === u.id ? (
                  <div style={{ display:"flex", alignItems:"center", gap:6, background:"#fffbeb", border:"1.5px solid #fde68a", borderRadius:10, padding:"4px 10px" }}>
                    <span style={{ fontSize:12, color:"#92400e", fontWeight:600 }}>
                      Change to {isAdminRole ? "User" : "Admin"}?
                    </span>
                    <button onClick={() => changeRole(u, isAdminRole ? "user" : "admin")}
                      style={{ background:"#f59e0b", border:"none", borderRadius:6, padding:"3px 10px", cursor:"pointer", fontSize:12, fontWeight:700, color:"#fff" }}>Yes</button>
                    <button onClick={() => setRoleConfirm(null)}
                      style={{ background:"#f3f4f6", border:"none", borderRadius:6, padding:"3px 8px", cursor:"pointer", fontSize:12, color:"#6b7280" }}>No</button>
                  </div>
                ) : (
                  <Btn size="sm" variant="ghost" onClick={() => setRoleConfirm(u.id)}
                    sx={{ borderColor: isAdminRole ? "#fde68a" : "#bfdbfe", color: isAdminRole ? "#92400e" : "#1e40af" }}>
                    {isAdminRole ? "👤 Set as User" : "🔐 Set as Admin"}
                  </Btn>
                )}
                {/* Force Logout */}
                {logoutConfirm === u.id ? (
                  <div style={{ display:"flex", alignItems:"center", gap:6, background:"#fef2f2", border:"1.5px solid #fecaca", borderRadius:10, padding:"4px 10px" }}>
                    <span style={{ fontSize:12, color:"#dc2626", fontWeight:600 }}>Force logout?</span>
                    <button onClick={() => forceLogoutUser(u)}
                      style={{ background:"#dc2626", border:"none", borderRadius:6, padding:"3px 10px", cursor:"pointer", fontSize:12, fontWeight:700, color:"#fff" }}>Yes</button>
                    <button onClick={() => setLogoutConfirm(null)}
                      style={{ background:"#f3f4f6", border:"none", borderRadius:6, padding:"3px 8px", cursor:"pointer", fontSize:12, color:"#6b7280" }}>No</button>
                  </div>
                ) : (
                  <Btn size="sm" variant="ghost" onClick={() => setLogoutConfirm(u.id)}
                    sx={{ borderColor:"#fecaca", color:"#dc2626" }}>
                    🚪 Logout
                  </Btn>
                )}
                <Btn size="sm" variant="danger" onClick={() => deleteUser(u)}><TrashIcon size={13} color="#fff" /></Btn>
              </div>
            </div>
          );
        })}
      </div>
      {showCreate && <CreateUserModal users={users} appUrl={appUrl} onCreated={() => {}} onClose={() => setShowCreate(false)} />}
      {resendUser && <CredShareModal user={resendUser} appUrl={appUrl} onClose={() => setResendUser(null)} />}
    </div>
  );
}

/* Task Form Modal */
function TaskModal({ task, users, projects, currentUser, onSave, onClose }) {
  const isEdit = !!task?.id;
  const [f, setF] = useState({
    id:            task?.id            || null,
    taskId:        task?.taskId        || `TASK-${String(Date.now()).slice(-6)}`,
    projectName:   task?.projectName   || "",
    taskName:      task?.taskName      || "",
    description:   task?.description   || "",
    assignedTo:    task?.assignedTo    || currentUser.name,   // always default to current user
    subAssigned:   task?.subAssigned   || [],                  // array of names
    priority:      task?.priority      || "Medium",
    status:        task?.status        || "Draft",
    deadline:      task?.deadline      || "",
    date:          task?.date          || today(),
    createdBy:     task?.createdBy     || currentUser.id,
    createdByName: task?.createdByName || currentUser.name,
    attachments:   task?.attachments   || [],
    startTime:     task?.startTime     || nowISO(),
    createdAt:     task?.createdAt     || nowISO(),
    endTime:       task?.endTime       || null,
    pausedDuration:  task?.pausedDuration  || 0,
    holdStartTime:   task?.holdStartTime   || null,
    draftDuration:   task?.draftDuration   || 0,
    draftStartTime:  task?.draftStartTime  || null,
  });
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const valid = f.projectName && f.taskName && f.assignedTo && f.deadline;

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const readers = files.map(file => new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve({
        name: file.name,
        size: file.size,
        type: file.type,
        data: ev.target.result, // base64
        addedAt: today(),
      });
      reader.readAsDataURL(file);
    }));
    Promise.all(readers).then(newFiles => {
      setF(p => ({ ...p, attachments: [...(p.attachments || []), ...newFiles] }));
      setUploading(false);
    });
    e.target.value = "";
  };

  const removeAttachment = (idx) => {
    setF(p => ({ ...p, attachments: p.attachments.filter((_, i) => i !== idx) }));
  };

  const fmtSize = (bytes) => bytes < 1024 ? bytes + " B" : bytes < 1048576 ? (bytes/1024).toFixed(1) + " KB" : (bytes/1048576).toFixed(1) + " MB";

  const fileIcon = (type) => {
    if (type?.startsWith("image/")) return "🖼";
    if (type?.includes("pdf")) return "📄";
    if (type?.includes("word") || type?.includes("document")) return "📝";
    if (type?.includes("sheet") || type?.includes("excel") || type?.includes("csv")) return "📊";
    if (type?.includes("zip") || type?.includes("rar")) return "🗜";
    return "📎";
  };

  return (
    <Overlay onClose={onClose} maxW={600}>
      <div style={{ padding: "26px 28px 28px" }}>
        <MHead title={isEdit ? "Edit Task" : "New Task"} sub="Fields marked * are required" onClose={onClose} />
        <div className="tf-modal-grid" style={{ marginTop:4 }}>
          <Field label="Task ID (auto)">
            <input value={f.taskId} readOnly style={{ ...inputCss, color: "#9ca3af", background: "#f9fafb" }} />
          </Field>
          <TInput label="Deadline *" type="date" value={f.deadline} min={today()} onChange={e => set("deadline", e.target.value)} />
          <div style={{ gridColumn: "1/-1" }}>
            <TSelect label="Project Name *" value={f.projectName} onChange={e => set("projectName", e.target.value)}
              options={[{ value: "", label: "Select project..." }, ...[...projects].sort((a,b) => a.name.localeCompare(b.name)).map(p => ({ value: p.name, label: p.name }))]} />
            {projects.length === 0 && <p style={{ margin: "5px 0 0 2px", fontSize: 11, color: "#dc2626" }}>⚠ No projects available. Ask admin to create a project first.</p>}
          </div>
          <div style={{ gridColumn: "1/-1" }}><TInput label="Task Name *" placeholder="e.g. Draft Budget Report" value={f.taskName} onChange={e => set("taskName", e.target.value)} /></div>

          {/* Description + Attachments */}
          <div style={{ gridColumn: "1/-1" }}>
            <Field label="Task Description">
              <textarea value={f.description} onChange={e => set("description", e.target.value)} placeholder="Describe the task..." style={{ ...inputCss, resize: "vertical", minHeight: 78 }} />
            </Field>

            {/* Attachment drop zone */}
            <div style={{ marginTop: 10 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Attachments</label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px", border: "2px dashed #d1d5db", borderRadius: 10, cursor: "pointer", background: "#fafafa", transition: "border-color .2s" }}
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor="#3b82f6"; }}
                onDragLeave={e => { e.currentTarget.style.borderColor="#d1d5db"; }}
                onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor="#d1d5db"; const dt = e.dataTransfer; if (dt.files.length) handleFiles({ target: { files: dt.files, value: "" } }); }}>
                <span style={{ fontSize: 22 }}>📎</span>
                <div>
                  <p style={{ margin: 0, fontSize: 13, color: "#374151", fontWeight: 600 }}>
                    {uploading ? "Reading files..." : "Click to attach files or drag & drop"}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9ca3af" }}>Images, PDFs, documents, spreadsheets — any file type</p>
                </div>
                <input type="file" multiple onChange={handleFiles} style={{ display: "none" }} />
              </label>

              {/* Attachment list */}
              {f.attachments?.length > 0 && (
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                  {f.attachments.map((att, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px" }}>
                      <span style={{ fontSize: 18, flexShrink: 0 }}>{fileIcon(att.type)}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#111", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.name}</p>
                        <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{fmtSize(att.size)}</p>
                      </div>
                      {att.type?.startsWith("image/") && (
                        <img src={att.data} alt={att.name} style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />
                      )}
                      <a href={att.data} download={att.name} style={{ flexShrink: 0, background: "#eff6ff", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 11, color: "#2563eb", fontWeight: 600, textDecoration: "none" }}>
                        ⬇ Download
                      </a>
                      <button onClick={() => removeAttachment(i)} style={{ flexShrink: 0, background: "#fef2f2", border: "none", borderRadius: 6, padding: "4px 6px", cursor: "pointer" }}>
                        <XIcon size={12} color="#dc2626" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <TSelect label="Assigned To *" value={f.assignedTo} onChange={e => set("assignedTo", e.target.value)} options={[{ value: "", label: "Select person..." }, ...[...users].sort((a,b) => a.name.localeCompare(b.name)).map(u => ({ value: u.name, label: u.name }))]} />

          {/* Sub Assigned — multi-select */}
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>Sub Assigned</label>
            <div style={{ border: "1.5px solid #e5e7eb", borderRadius: 10, background: "#fff", maxHeight: 130, overflowY: "auto", padding: "6px 8px", display: "flex", flexDirection: "column", gap: 3 }}>
              {[...users].sort((a,b) => a.name.localeCompare(b.name)).map(u => {
                const checked = (f.subAssigned || []).includes(u.name);
                return (
                  <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 6px", borderRadius: 7, cursor: "pointer", background: checked ? "#eff6ff" : "transparent" }}>
                    <input type="checkbox" checked={checked}
                      onChange={e => {
                        const cur = f.subAssigned || [];
                        set("subAssigned", e.target.checked ? [...cur, u.name] : cur.filter(n => n !== u.name));
                      }}
                      style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#2563eb" }} />
                    <Avatar name={u.name} size={20} />
                    <span style={{ fontSize: 12, color: checked ? "#1e40af" : "#374151", fontWeight: checked ? 600 : 400 }}>{u.name}</span>
                    {checked && <span style={{ marginLeft: "auto", fontSize: 10, color: "#2563eb", fontWeight: 700 }}>✓</span>}
                  </label>
                );
              })}
              {users.length === 0 && <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", padding: "4px 6px" }}>No team members</p>}
            </div>
            {(f.subAssigned?.length > 0) && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 5 }}>
                {f.subAssigned.map(name => (
                  <span key={name} style={{ fontSize: 11, background: "#eff6ff", color: "#1e40af", padding: "2px 8px", borderRadius: 6, fontWeight: 600 }}>
                    {name}
                    <button onClick={() => set("subAssigned", f.subAssigned.filter(n => n !== name))}
                      style={{ background: "none", border: "none", cursor: "pointer", marginLeft: 4, color: "#6b7280", fontSize: 11, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <TSelect label="Priority" value={f.priority} onChange={e => set("priority", e.target.value)} options={["High", "Medium", "Low"]} />
          <TSelect label="Status" value={f.status} onChange={e => set("status", e.target.value)} options={ALL_STATUSES} />
          {currentUser.role === "admin" ? (
            <TInput label="Task Date" type="date" value={f.date} onChange={e => set("date", e.target.value)} />
          ) : (
            <Field label="Task Date">
              <input value={f.date} readOnly style={{ ...inputCss, color: "#6b7280", background: "#f9fafb", cursor: "not-allowed" }} title="Only admin can change task date" />
            </Field>
          )}

          {/* Start Time & Finish Time — admin only */}
          {currentUser.role === "admin" ? (
            <>
              <Field label="Start Time (Admin)">
                <input type="datetime-local" value={f.startTime ? f.startTime.slice(0,16) : ""}
                  onChange={e => set("startTime", e.target.value ? new Date(e.target.value).toISOString() : null)}
                  style={{ ...inputCss, padding: "9px 12px" }} />
              </Field>
              <Field label="Finish Time (Admin)">
                <input type="datetime-local" value={f.endTime ? f.endTime.slice(0,16) : ""}
                  onChange={e => set("endTime", e.target.value ? new Date(e.target.value).toISOString() : null)}
                  style={{ ...inputCss, padding: "9px 12px" }} />
              </Field>
            </>
          ) : (
            <>
              <Field label="Start Time">
                <input value={f.startTime ? fmtTime(f.startTime) : "—"} readOnly style={{ ...inputCss, color: "#6b7280", background: "#f9fafb", cursor: "not-allowed" }} title="Only admin can edit start time" />
              </Field>
              <Field label="Finish Time">
                <input value={f.endTime ? fmtTime(f.endTime) : "—"} readOnly style={{ ...inputCss, color: "#6b7280", background: "#f9fafb", cursor: "not-allowed" }} title="Only admin can edit finish time" />
              </Field>
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 22 }}>
          <Btn onClick={onClose} variant="ghost">Cancel</Btn>
          <Btn onClick={() => valid && onSave(f)} disabled={!valid} variant="primary">{isEdit ? "Save Changes" : "Create Task"}</Btn>
        </div>
      </div>
    </Overlay>
  );
}

/* Task Card */
function TaskCard({ task, canEdit, onEdit, onDelete, onShare, onStatusChange }) {
  const pc   = PRIORITY_CFG[task.priority] || PRIORITY_CFG.Medium;
  const sc   = STATUS_CFG[task.status]     || STATUS_CFG["Draft"];
  const over = isOver(task);
  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "18px 20px", border: `1.5px solid ${over ? "#fecaca" : "#f0f0f0"}`, boxShadow: over ? "0 2px 12px rgba(239,68,68,.07)" : "0 2px 8px rgba(0,0,0,.04)", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 5 }}>
            <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>{task.taskId}</span>
            <Pill color={pc.color} bg={pc.bg} border={pc.border}>● {task.priority}</Pill>
            {over && <Pill color="#dc2626" bg="#fef2f2" border="#fecaca">⚠ Overdue</Pill>}
            {task.status === "Hold" && <Pill color="#b45309" bg="#fffbeb" border="#fde68a">⏸ On Hold</Pill>}
          </div>
          <p style={{ margin: "0 0 3px", fontWeight: 700, fontSize: 15, color: "#111", lineHeight: 1.3 }}>{task.taskName}</p>
          <p style={{ margin: 0, fontSize: 12, color: "#2563eb", fontWeight: 600 }}>📁 {task.projectName}</p>
        </div>
        <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
          <button onClick={() => onShare(task)} style={{ background: "#f0fdf4", border: "none", borderRadius: 8, padding: "6px 7px", cursor: "pointer" }}><ShareIcon size={14} color="#10b981" /></button>
          {canEdit && <>
            <button onClick={() => onEdit(task)} style={{ background: "#eff6ff", border: "none", borderRadius: 8, padding: "6px 7px", cursor: "pointer" }}><EditIcon size={14} color="#2563eb" /></button>
            <button onClick={() => onDelete(task.id)} style={{ background: "#fef2f2", border: "none", borderRadius: 8, padding: "6px 7px", cursor: "pointer" }}><TrashIcon size={14} color="#dc2626" /></button>
          </>}
        </div>
      </div>
      {task.description && <p style={{ margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.55, borderLeft: "3px solid #e5e7eb", paddingLeft: 10 }}>{task.description}</p>}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, background: "#f3f4f6", borderRadius: 8, padding: "4px 10px" }}>
            <span style={{ fontSize: 12 }}>👤</span><span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{task.assignedTo}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, background: over ? "#fef2f2" : "#f3f4f6", borderRadius: 8, padding: "4px 10px" }}>
            <CalIcon size={12} color={over ? "#dc2626" : "#6b7280"} />
            <span style={{ fontSize: 12, fontWeight: 600, color: over ? "#dc2626" : "#374151" }}>{fmtDate(task.deadline)}</span>
          </div>
        </div>
        <select value={task.status} onChange={e => onStatusChange(task.id, e.target.value)}
          style={{ padding: "5px 10px", borderRadius: 8, border: `1.5px solid ${sc.border}`, background: sc.bg, color: sc.color, fontSize: 12, fontWeight: 700, cursor: "pointer", outline: "none", fontFamily: "inherit" }}>
          {ALL_STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
    </div>
  );
}

/* Stats Bar */
/* ─────────────────────────────────────────────
   ATTENDANCE PANEL
   Shows per-user attendance derived from task
   submission dates, calendar heatmap, tabulation,
   and performance charts.
───────────────────────────────────────────── */
/* ─────────────────────────────────────────────
   PROFILE MODAL — edit own profile (name, dept, vertical)
───────────────────────────────────────────── */
function ProfileModal({ user, onSave, onClose }) {
  const [f, setF] = useState({
    name:       user.name       || "",
    department: user.department || "",
    vertical:   user.vertical   || "",
    phone:      user.phone      || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return;
    setSaving(true);
    const updated = { ...user, ...f, name: f.name.trim() };
    await DB.set("users", user.id, updated);
    // update session
    try { localStorage.setItem("tf_session_user", JSON.stringify(updated)); } catch(e) {}
    onSave(updated);
    setSaving(false);
  };

  const overlay = { position:"fixed",inset:0,background:"rgba(0,0,0,.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16 };
  const modal   = { background:"#fff",borderRadius:20,padding:"28px 28px 24px",width:"100%",maxWidth:440,boxShadow:"0 24px 60px rgba(0,0,0,.18)" };

  return (
    <div style={overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={modal}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:22}}>
          <div>
            <h3 style={{margin:0,fontSize:18,fontWeight:800,color:"#0f172a"}}>Edit Profile</h3>
            <p style={{margin:"2px 0 0",fontSize:12,color:"#9ca3af"}}>Update your personal information</p>
          </div>
          <button onClick={onClose} style={{background:"#f3f4f6",border:"none",borderRadius:8,padding:"6px 8px",cursor:"pointer"}}><XIcon size={16} color="#6b7280"/></button>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {/* Avatar preview */}
          <div style={{display:"flex",justifyContent:"center",marginBottom:4}}>
            <Avatar name={f.name||user.name} size={64}/>
          </div>

          {[
            {label:"Full Name *",      key:"name",       placeholder:"Your full name",      type:"text"},
            {label:"Department",       key:"department", placeholder:"e.g. Marketing, IT",  type:"text"},
            {label:"Vertical / Team",  key:"vertical",   placeholder:"e.g. Digital, Sales", type:"text"},
            {label:"Phone",            key:"phone",      placeholder:"+971 xx xxx xxxx",    type:"tel"},
          ].map(fi=>(
            <div key={fi.key}>
              <label style={{display:"block",fontSize:11,fontWeight:700,color:"#6b7280",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.05em"}}>{fi.label}</label>
              <input
                type={fi.type}
                value={f[fi.key]}
                onChange={e=>set(fi.key, e.target.value)}
                placeholder={fi.placeholder}
                style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1.5px solid #e5e7eb",fontSize:13,color:"#111",outline:"none",boxSizing:"border-box",fontFamily:"inherit"}}
                onFocus={e=>e.target.style.borderColor="#3b82f6"}
                onBlur={e=>e.target.style.borderColor="#e5e7eb"}
              />
            </div>
          ))}

          {/* Read-only fields */}
          <div style={{background:"#f8fafc",borderRadius:10,padding:"12px 14px",display:"flex",flexDirection:"column",gap:6}}>
            <p style={{margin:0,fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>Account Info (read-only)</p>
            <p style={{margin:0,fontSize:12,color:"#6b7280"}}>📧 {user.email}</p>
            <p style={{margin:0,fontSize:12,color:"#6b7280"}}>🔑 Role: <strong>{user.role==="admin"?"Administrator":"Team Member"}</strong></p>
            {user.location && <p style={{margin:0,fontSize:12,color:"#6b7280"}}>📍 {user.location}</p>}
          </div>
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:22}}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={saving||!f.name.trim()}>
            {saving?"Saving...":"Save Profile"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   ATTENDANCE & PERFORMANCE PANEL
───────────────────────────────────────────── */
function AttendancePanel({ users, tasks, attendance, currentUser }) {
  const todayObj    = new Date();
  const todayStr    = todayObj.toISOString().slice(0, 10);
  const firstOfMonth= `${todayObj.getFullYear()}-${String(todayObj.getMonth()+1).padStart(2,"0")}-01`;

  const [view,      setView]      = useState("overview");
  const [selUser,   setSelUser]   = useState("all");
  const [dateFrom,  setDateFrom]  = useState(firstOfMonth);
  const [dateTo,    setDateTo]    = useState(todayStr);

  const monthOpts = Array.from({length:12},(_,i)=>{
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth()-i);
    const y = d.getFullYear(), mo = d.getMonth(), m = String(mo+1).padStart(2,"0");
    const daysIn = new Date(y, mo+1, 0).getDate();
    const isCur  = i === 0;
    return { value:`${y}-${m}`, from:`${y}-${m}-01`,
      to: isCur ? todayStr : `${y}-${m}-${String(daysIn).padStart(2,"0")}`,
      label: d.toLocaleDateString("en-US",{month:"long",year:"numeric"}) + (isCur?" (current)":"") };
  });

  const allUsers = users.filter(u => u.id !== "admin");

  // ── Range days & workdays ─────────────────────────────
  const rangeDays = (() => {
    const days=[]; const cur=new Date(dateFrom+"T00:00:00"); const end=new Date(dateTo+"T00:00:00");
    while(cur<=end){ days.push(cur.toISOString().slice(0,10)); cur.setDate(cur.getDate()+1); }
    return days;
  })();
  const workdays = rangeDays.filter(d=>{ const wd=new Date(d+"T00:00:00").getDay(); return wd!==0&&wd!==6; });

  // ── Attendance maps ───────────────────────────────────
  const attendanceMap = {}; // userId → Set<dateStr>
  const loginMeta     = {}; // userId → { dateStr → { loginTime, location, workLocation, sources } }

  attendance.forEach(rec => {
    if (!rec.userId || !rec.date) return;
    if (!attendanceMap[rec.userId]) attendanceMap[rec.userId] = new Set();
    attendanceMap[rec.userId].add(rec.date);
    if (!loginMeta[rec.userId]) loginMeta[rec.userId] = {};
    loginMeta[rec.userId][rec.date] = {
      loginTime:    rec.loginTime,
      location:     rec.location,
      workLocation: rec.workLocation,
      sources:      rec.sources || {},
    };
  });

  // ── Tasks filtered to range (non-Draft, non-Hold) ─────
  const tasksByUser = {};
  tasks.forEach(t => {
    const uid  = t.createdBy;
    const date = t.date || (t.createdAt ? t.createdAt.slice(0,10) : null);
    if (!uid || !date || date < dateFrom || date > dateTo) return;
    if (t.status === "Draft" || t.status === "Hold") return; // exclude Draft & Hold
    if (!tasksByUser[uid]) tasksByUser[uid] = [];
    tasksByUser[uid].push(t);
  });

  // ── calcAvgDur ────────────────────────────────────────
  const calcAvgDur = (tList) => {
    const completed = tList.filter(t=>t.startTime&&t.endTime&&t.status==="Completed");
    if (!completed.length) return null;
    const avgMs = completed.reduce((s,t)=>s+Math.max(0,
      new Date(t.endTime)-new Date(t.startTime)-(t.pausedDuration||0)-(t.draftDuration||0)
    ),0)/completed.length;
    const m=Math.floor(avgMs/60000);
    return m<60?`${m}m`:`${Math.floor(m/60)}h ${m%60}m`;
  };

  // ── Per-user stats ────────────────────────────────────
  const buildStats = (u) => {
    const wdPresent  = workdays.filter(d => attendanceMap[u.id]?.has(d));
    const allPresent = rangeDays.filter(d => attendanceMap[u.id]?.has(d));
    const uTasks     = tasksByUser[u.id] || [];
    const done       = uTasks.filter(t=>t.status==="Completed").length;
    const inProg     = uTasks.filter(t=>t.status==="In Progress").length;
    const hold       = uTasks.filter(t=>t.status==="Hold").length;
    const draft      = uTasks.filter(t=>t.status==="Draft").length;
    const review     = uTasks.filter(t=>t.status==="Review").length;
    const pct        = workdays.length ? Math.round(wdPresent.length/workdays.length*100) : 0;
    const compPct    = uTasks.length   ? Math.round(done/uTasks.length*100) : 0;
    // workLocation breakdown
    const offDays    = allPresent.filter(d=>loginMeta[u.id]?.[d]?.workLocation==="Office").length;
    const wfhDays    = allPresent.filter(d=>loginMeta[u.id]?.[d]?.workLocation==="Work From Home").length;
    const lastLogin  = [...allPresent].sort().reverse()[0] || null;
    const lastMeta   = lastLogin ? loginMeta[u.id]?.[lastLogin] : null;
    return {
      user:u, present:allPresent.length, wdPresent:wdPresent.length,
      total:uTasks.length, done, inProg, hold, draft, review,
      avgDur:calcAvgDur(uTasks), pct, compPct,
      presentDays:new Set(allPresent), offDays, wfhDays,
      lastLogin, lastMeta,
    };
  };
  const userStats = allUsers.map(buildStats);

  // ── Group rollups (dept/vertical) ─────────────────────
  const groupBy = (key) => {
    const map={};
    userStats.forEach(s=>{
      const grp=s.user[key]||"Unassigned";
      if(!map[grp]) map[grp]={name:grp,members:[],wdPresent:0,workdaySlots:0,total:0,done:0,inProg:0};
      map[grp].members.push(s.user.name);
      map[grp].wdPresent+=s.wdPresent; map[grp].workdaySlots+=workdays.length;
      map[grp].total+=s.total; map[grp].done+=s.done; map[grp].inProg+=s.inProg;
    });
    return Object.values(map).map(g=>({...g,
      pct:     g.workdaySlots?Math.round(g.wdPresent/g.workdaySlots*100):0,
      compPct: g.total?Math.round(g.done/g.total*100):0,
    })).sort((a,b)=>b.pct-a.pct);
  };
  const deptStats     = groupBy("department");
  const verticalStats = groupBy("vertical");

  // ── Team totals ───────────────────────────────────────
  const teamTotal={total:0,done:0,inProg:0,draft:0,pct:0,wdPresent:0,compPct:0};
  userStats.forEach(s=>{
    teamTotal.total+=s.total; teamTotal.done+=s.done; teamTotal.inProg+=s.inProg;
    teamTotal.draft+=s.draft; teamTotal.wdPresent+=s.wdPresent;
  });
  teamTotal.pct     = userStats.length?Math.round(userStats.reduce((s,u)=>s+u.pct,0)/userStats.length):0;
  teamTotal.compPct = teamTotal.total?Math.round(teamTotal.done/teamTotal.total*100):0;

  // ── Calendar display helpers ──────────────────────────
  const calYear  = parseInt(dateFrom.slice(0,4));
  const calMonth = parseInt(dateFrom.slice(5,7));
  const daysInCal= new Date(calYear,calMonth,0).getDate();
  const firstWd  = new Date(calYear,calMonth-1,1).getDay();
  const calDays  = Array.from({length:daysInCal},(_,i)=>
    `${calYear}-${String(calMonth).padStart(2,"0")}-${String(i+1).padStart(2,"0")}`
  );
  const MONTH_NAMES=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // ── Color helpers ─────────────────────────────────────
  const pctColor = p=>p>=80?"#059669":p>=60?"#d97706":p>=40?"#f59e0b":"#dc2626";
  const pctBg    = p=>p>=80?"#f0fdf4":p>=60?"#fffbeb":p>=40?"#fef9c3":"#fef2f2";
  const heatColor= n=>n===0?"#f0f4f8":n===1?"#bfdbfe":n<=3?"#60a5fa":n<=6?"#2563eb":"#1e3a8a";
  const wlocColor= wl=>wl==="Office"?"#059669":wl==="Work From Home"?"#7c3aed":"#9ca3af";
  const wlocBg   = wl=>wl==="Office"?"#f0fdf4":wl==="Work From Home"?"#f5f3ff":"#f9fafb";
  const wlocIcon = wl=>wl==="Office"?"🏢":wl==="Work From Home"?"🏠":"📍";

  // ── Donut ─────────────────────────────────────────────
  const Donut=({pct,size=64,color="#3b82f6"})=>{
    const r=26,cx=32,cy=32,circ=2*Math.PI*r;
    const dash=circ*Math.min(pct,100)/100,gap=circ-dash;
    return(<svg width={size} height={size} viewBox="0 0 64 64">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#f0f0f0" strokeWidth="8"/>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth="8"
        strokeDasharray={`${dash} ${gap}`} strokeDashoffset={circ/4} strokeLinecap="round"/>
      <text x={cx} y={cy+4} textAnchor="middle" fontSize="12" fontWeight="700" fill={color}>{pct}%</text>
    </svg>);
  };

  // ── Bar chart ─────────────────────────────────────────
  const BarChart=({data,maxVal,h=140})=>{
    const w=38,gap=8,padL=28,padB=24;
    const totalW=Math.max(200,data.length*(w+gap)+padL+10);
    const scale=v=>maxVal>0?(v/maxVal)*(h-padB):0;
    return(<svg width={totalW} height={h+8} style={{overflow:"visible",display:"block"}}>
      {[0,25,50,75,100].map(v=>{const yy=h-padB-scale(maxVal*v/100);return<line key={v} x1={padL} x2={totalW} y1={yy} y2={yy} stroke="#f3f4f6" strokeWidth="1"/>;})}
      {data.map((d,i)=>{
        const bh=Math.max(2,scale(d.val)),x=padL+i*(w+gap),y=h-padB-bh;
        return(<g key={i}>
          <rect x={x} y={y} width={w} height={bh} rx={4} fill={d.color||"#3b82f6"} opacity={.85}/>
          <text x={x+w/2} y={y-4} textAnchor="middle" fontSize={9} fill="#374151">{d.val}{d.suffix||""}</text>
          <text x={x+w/2} y={h-padB+14} textAnchor="middle" fontSize={9} fill="#9ca3af">
            {d.label.length>6?d.label.slice(0,6)+"…":d.label}
          </text>
        </g>);
      })}
    </svg>);
  };

  // ── Hourly Heatmap ────────────────────────────────────
  // Shows task activity per hour of day across the selected range
  const HourlyHeatmap = ({ statOrNull }) => {
    const hourCounts = Array(24).fill(0);
    const sourceTasks = statOrNull
      ? (tasksByUser[statOrNull.user.id] || [])
      : tasks.filter(t => t.date >= dateFrom && t.date <= dateTo && t.status !== "Draft");

    sourceTasks.forEach(t => {
      const ts = t.startTime || t.createdAt;
      if (ts) { const h = new Date(ts).getHours(); if (h>=0&&h<24) hourCounts[h]++; }
      if (t.endTime) { const h = new Date(t.endTime).getHours(); if (h>=0&&h<24) hourCounts[h]++; }
    });

    const sourceRecs = statOrNull
      ? attendance.filter(r=>r.userId===statOrNull.user.id&&r.date>=dateFrom&&r.date<=dateTo)
      : attendance.filter(r=>r.date>=dateFrom&&r.date<=dateTo);
    sourceRecs.forEach(r=>{
      if(r.loginTime){ const h=new Date(r.loginTime).getHours(); if(h>=0&&h<24) hourCounts[h]++; }
    });

    const maxH = Math.max(1,...hourCounts);
    const cellW = 28, cellH = 22, padTop = 8;
    const blocks = [
      {label:"Early",    hours:[0,1,2,3,4,5,6,7],    color:"#e0e7ff"},
      {label:"Morning",  hours:[8,9,10,11],            color:"#bfdbfe"},
      {label:"Noon",     hours:[12,13],                color:"#60a5fa"},
      {label:"Afternoon",hours:[14,15,16,17],          color:"#2563eb"},
      {label:"Evening",  hours:[18,19,20,21,22,23],   color:"#1e3a8a"},
    ];
    const fmtH = h => h===0?"12a":h<12?`${h}a`:h===12?"12p":`${h-12}p`;
    const peakHour = hourCounts.indexOf(maxH);

    return (
      <div style={{display:"flex",flexDirection:"column",gap:16}}>
        {/* ── Heatmap row ── */}
        <div>
          <p style={{margin:"0 0 6px",fontSize:11,fontWeight:700,color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.05em"}}>Heatmap — intensity by hour</p>
          <div style={{overflowX:"auto"}}>
            <svg width={24*(cellW+2)+8} height={cellH+padTop+32} style={{display:"block"}}>
              {hourCounts.map((n,h)=>{
                const intensity=n/maxH;
                const bg=n===0?"#f8fafc":`rgba(37,99,235,${0.1+intensity*0.9})`;
                const x=h*(cellW+2);
                return(<g key={h}>
                  <rect x={x} y={padTop} width={cellW} height={cellH} rx={3} fill={bg}/>
                  {n>0&&<text x={x+cellW/2} y={padTop+cellH/2+4} textAnchor="middle" fontSize={8} fill={intensity>0.5?"#fff":"#374151"} fontWeight="600">{n}</text>}
                  <text x={x+cellW/2} y={padTop+cellH+12} textAnchor="middle" fontSize={8} fill="#9ca3af">{fmtH(h)}</text>
                </g>);
              })}
            </svg>
          </div>
        </div>

        {/* ── Bar chart ── */}
        <div>
          <p style={{margin:"0 0 6px",fontSize:11,fontWeight:700,color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.05em"}}>Bar chart — activity count per hour</p>
          <div style={{overflowX:"auto"}}>
            {(() => {
              const barW=28, barGap=2, chartH=80, padB=18;
              const totalW=24*(barW+barGap);
              return (
                <svg width={totalW} height={chartH+padB+4} style={{display:"block"}}>
                  {/* gridlines */}
                  {[0,0.25,0.5,0.75,1].map(f=>{
                    const y=chartH-Math.round(f*chartH);
                    return <line key={f} x1={0} x2={totalW} y1={y} y2={y} stroke="#f0f0f0" strokeWidth="1"/>;
                  })}
                  {hourCounts.map((n,h)=>{
                    const bh=Math.max(n>0?3:1, Math.round(n/maxH*chartH));
                    const x=h*(barW+barGap);
                    const y=chartH-bh;
                    const isWork=h>=8&&h<=18;
                    const isPeak=h===peakHour&&n>0;
                    const fill=isPeak?"#dc2626":isWork?"#2563eb":"#94a3b8";
                    return(<g key={h}>
                      <rect x={x} y={y} width={barW} height={bh} rx={3} fill={fill} opacity={0.85}/>
                      {n>0&&<text x={x+barW/2} y={y-3} textAnchor="middle" fontSize={8} fill={isPeak?"#dc2626":"#374151"} fontWeight="600">{n}</text>}
                      <text x={x+barW/2} y={chartH+padB} textAnchor="middle" fontSize={8} fill={isPeak?"#dc2626":"#9ca3af"} fontWeight={isPeak?"700":"400"}>{fmtH(h)}</text>
                    </g>);
                  })}
                </svg>
              );
            })()}
          </div>
          <div style={{display:"flex",gap:12,marginTop:6,flexWrap:"wrap",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:2,background:"#dc2626"}}/><span style={{fontSize:10,color:"#6b7280"}}>Peak hour ({fmtH(peakHour)})</span></div>
            <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:2,background:"#2563eb"}}/><span style={{fontSize:10,color:"#6b7280"}}>Work hours (8a–6p)</span></div>
            <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:10,height:10,borderRadius:2,background:"#94a3b8"}}/><span style={{fontSize:10,color:"#6b7280"}}>Off hours</span></div>
          </div>
        </div>

        {/* ── Block summary ── */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {blocks.map(b=>{
            const total=b.hours.reduce((s,h)=>s+hourCounts[h],0);
            return(<div key={b.label} style={{display:"flex",alignItems:"center",gap:4,background:"#f8fafc",borderRadius:6,padding:"3px 8px"}}>
              <div style={{width:8,height:8,borderRadius:2,background:b.color}}/>
              <span style={{fontSize:10,color:"#6b7280"}}>{b.label}:</span>
              <span style={{fontSize:10,color:"#374151",fontWeight:700}}>{total}</span>
            </div>);
          })}
          {maxH>0&&<div style={{marginLeft:"auto",fontSize:11,color:"#dc2626",fontWeight:700}}>Peak: {fmtH(peakHour)} ({maxH} activities)</div>}
        </div>
      </div>
    );
  };

  // ── UserCalendar ──────────────────────────────────────
  const UserCalendar = ({ stat }) => {
    const taskCounts={};
    (tasksByUser[stat.user.id]||[]).forEach(t=>{
      const d=t.date||(t.createdAt?t.createdAt.slice(0,10):null);
      if(d&&d>=dateFrom&&d<=dateTo) taskCounts[d]=(taskCounts[d]||0)+1;
    });
    const cells=[]; for(let i=0;i<firstWd;i++) cells.push(null);
    calDays.forEach(d=>cells.push(d));
    return(
      <div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
          {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d=>(
            <div key={d} style={{textAlign:"center",fontSize:10,color:"#9ca3af",fontWeight:700,padding:"2px 0"}}>{d}</div>
          ))}
          {cells.map((d,i)=>{
            if(!d) return <div key={`e${i}`}/>;
            const n=taskCounts[d]||0;
            const inRange=d>=dateFrom&&d<=dateTo;
            const isToday=d===todayStr;
            const meta=loginMeta[stat.user.id]?.[d];
            const loggedIn=!!meta;
            const wl=meta?.workLocation||"";
            const src=meta?.sources||{};
            const loginStr=meta?.loginTime?fmtTime(meta.loginTime):"";
            const srcLabel=src.login&&src.task?"Login+Task":src.login?"Logged in "+loginStr:"Task only";
            const tooltip=loggedIn?`✅ Present — ${srcLabel}${wl?" · "+wl:""}${n?" · "+n+" task"+(n!==1?"s":""):""}`:
              `${d}${n?": "+n+" task"+(n!==1?"s":""):" · no activity"}`;
            // Color by workLocation when present
            const bg=!inRange?"#f9fafb":
              loggedIn&&wl==="Office"?"#dcfce7":
              loggedIn&&wl==="Work From Home"?"#ede9fe":
              loggedIn&&n>0?heatColor(n):
              loggedIn?"#dcfce7":"#f0f4f8";
            return(
              <div key={d} title={tooltip} style={{
                aspectRatio:"1",borderRadius:4,background:bg,
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:9,color:loggedIn&&n>3?"#fff":"#374151",fontWeight:loggedIn?700:400,
                border:isToday?"2px solid #f59e0b":loggedIn&&!n?"1.5px solid #86efac":"1.5px solid transparent",
                opacity:!inRange?0.3:1,position:"relative",cursor:"default"
              }}>
                {new Date(d+"T00:00:00").getDate()}
                {n>0&&inRange&&<span style={{position:"absolute",bottom:0,right:2,fontSize:6,color:n>3?"rgba(255,255,255,.7)":"#6b7280"}}>{n}</span>}
                {loggedIn&&n===0&&inRange&&<span style={{position:"absolute",bottom:0,right:1,fontSize:6,color:"#16a34a"}}>✓</span>}
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:4,alignItems:"center"}}>
          {[["#dcfce7","Office","#86efac"],["#ede9fe","WFH",null],["#60a5fa","Tasks",null],["#f0f4f8","Absent",null]].map(([bg,l,bdr])=>(
            <div key={l} style={{display:"flex",alignItems:"center",gap:3}}>
              <div style={{width:9,height:9,borderRadius:2,background:bg,border:bdr?`1px solid ${bdr}`:"none"}}/>
              <span style={{fontSize:9,color:"#9ca3af"}}>{l}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const card=(children,sx={})=>(
    <div style={{background:"#fff",borderRadius:16,padding:"18px 20px",border:"1.5px solid #f0f0f0",boxShadow:"0 2px 8px rgba(0,0,0,.04)",...sx}}>{children}</div>
  );
  const sectionTitle=(t,sub)=>(
    <div style={{marginBottom:14}}>
      <h3 style={{margin:"0 0 2px",fontSize:15,fontWeight:800,color:"#0f172a"}}>{t}</h3>
      {sub&&<p style={{margin:0,fontSize:11,color:"#9ca3af"}}>{sub}</p>}
    </div>
  );

  const visStats = selUser==="all" ? userStats : userStats.filter(s=>s.user.id===selUser);
  const rangeLabel=`${dateFrom} → ${dateTo}  (${workdays.length} working days)`;

  const GroupCard=({g,icon})=>(
    <div style={{background:"#fff",borderRadius:14,padding:"16px 18px",border:"1.5px solid #f0f0f0"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
        <span style={{fontSize:18}}>{icon}</span>
        <div style={{flex:1}}>
          <p style={{margin:0,fontWeight:700,fontSize:14,color:"#111"}}>{g.name}</p>
          <p style={{margin:0,fontSize:11,color:"#9ca3af"}}>{g.members.length} member{g.members.length!==1?"s":""}</p>
        </div>
        <div style={{background:pctBg(g.pct),borderRadius:8,padding:"3px 10px"}}>
          <span style={{fontSize:13,fontWeight:800,color:pctColor(g.pct)}}>{g.pct}%</span>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
        {[{l:"Attendance",v:g.pct+"%",c:pctColor(g.pct)},{l:"Tasks",v:g.total,c:"#2563eb"},{l:"Done",v:g.done,c:"#059669"},{l:"Completion",v:g.compPct+"%",c:"#7c3aed"}].map(x=>(
          <div key={x.l} style={{textAlign:"center",background:"#f8fafc",borderRadius:8,padding:"6px 3px"}}>
            <p style={{margin:0,fontWeight:800,fontSize:14,color:x.c}}>{x.v}</p>
            <p style={{margin:0,fontSize:9,color:"#9ca3af",fontWeight:600,textTransform:"uppercase"}}>{x.l}</p>
          </div>
        ))}
      </div>
      <p style={{margin:"8px 0 0",fontSize:10,color:"#9ca3af",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{g.members.join(" · ")}</p>
    </div>
  );

  return (
    <div>
      {/* ── Header ── */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:18,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{margin:"0 0 3px",fontSize:22,fontWeight:800,color:"#0f172a"}}>Attendance & Performance</h2>
          <p style={{margin:0,fontSize:12,color:"#9ca3af"}}>{rangeLabel}</p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <select value="" onChange={e=>{const o=monthOpts.find(m=>m.value===e.target.value);if(o){setDateFrom(o.from);setDateTo(o.to);}}}
            style={{padding:"8px 12px",borderRadius:10,border:"1.5px solid #e5e7eb",fontSize:13,background:"#fafafa",cursor:"pointer"}}>
            <option value="">📅 Select Month</option>
            {monthOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{display:"flex",alignItems:"center",gap:4,background:"#f8fafc",borderRadius:10,padding:"4px 10px",border:"1.5px solid #e5e7eb"}}>
            <span style={{fontSize:11,color:"#9ca3af",fontWeight:600}}>FROM</span>
            <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
              style={{border:"none",background:"transparent",fontSize:12,color:"#374151",outline:"none",cursor:"pointer"}}/>
            <span style={{fontSize:11,color:"#9ca3af",fontWeight:600}}>TO</span>
            <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
              style={{border:"none",background:"transparent",fontSize:12,color:"#374151",outline:"none",cursor:"pointer"}}/>
          </div>
          <select value={selUser} onChange={e=>setSelUser(e.target.value)}
            style={{padding:"8px 12px",borderRadius:10,border:"1.5px solid #e5e7eb",fontSize:13,background:"#fafafa",cursor:"pointer"}}>
            <option value="all">👥 All Members</option>
            {[...allUsers].sort((a,b)=>a.name.localeCompare(b.name)).map(u=>(
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{display:"flex",background:"#f1f5f9",borderRadius:12,padding:3,gap:2,marginBottom:20,overflowX:"auto",width:"fit-content"}}>
        {[["overview","📊 Overview"],["attendance","🗓 Attendance"],["calendar","📅 Calendar"],["table","📋 Tabulation"],["chart","📈 Performance"]].map(([k,l])=>(
          <button key={k} onClick={()=>setView(k)} style={{
            padding:"8px 16px",borderRadius:10,border:"none",fontSize:13,fontWeight:view===k?700:500,
            cursor:"pointer",background:view===k?"#fff":"transparent",
            color:view===k?"#1e40af":"#6b7280",
            boxShadow:view===k?"0 1px 4px rgba(0,0,0,.1)":"none",whiteSpace:"nowrap"
          }}>{l}</button>
        ))}
      </div>

      {/* ══ OVERVIEW ══ */}
      {view==="overview"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))",gap:10,marginBottom:20}}>
            {[
              {label:"Members",        val:allUsers.length,        icon:"👥",c:"#2563eb",bg:"#eff6ff"},
              {label:"Working Days",   val:workdays.length,         icon:"📅",c:"#7c3aed",bg:"#f5f3ff"},
              {label:"Total Tasks",    val:teamTotal.total,         icon:"📋",c:"#059669",bg:"#f0fdf4"},
              {label:"Completed",      val:teamTotal.done,          icon:"✅",c:"#16a34a",bg:"#f0fdf4"},
              {label:"Team Attendance",val:teamTotal.pct+"%",       icon:"📈",c:"#d97706",bg:"#fffbeb"},
              {label:"Task Completion",val:teamTotal.compPct+"%",   icon:"🎯",c:"#7c3aed",bg:"#f5f3ff"},
            ].map(s=>(
              <div key={s.label} style={{background:"#fff",borderRadius:12,padding:"14px 16px",border:"1.5px solid #f0f0f0",display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:36,height:36,borderRadius:10,background:s.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>{s.icon}</div>
                <div>
                  <p style={{margin:0,fontWeight:800,fontSize:20,color:s.c,lineHeight:1}}>{s.val}</p>
                  <p style={{margin:"2px 0 0",fontSize:10,color:"#9ca3af",fontWeight:600}}>{s.label}</p>
                </div>
              </div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14}}>
            {visStats.map(s=>(
              <div key={s.user.id} style={{background:"#fff",borderRadius:16,padding:"18px 20px",border:"1.5px solid #f0f0f0",boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                  <Avatar name={s.user.name} size={42}/>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{margin:0,fontWeight:700,fontSize:14,color:"#111"}}>{s.user.name}</p>
                    <p style={{margin:"1px 0 0",fontSize:11,color:"#9ca3af"}}>{s.user.role==="admin"?"🔐 Admin":"👤 User"}</p>
                    {s.user.department&&<p style={{margin:"1px 0 0",fontSize:10,color:"#6b7280"}}>🏢 {s.user.department}</p>}
                    {s.user.vertical&&<p style={{margin:"1px 0 0",fontSize:10,color:"#6b7280"}}>⬆️ {s.user.vertical}</p>}
                    {s.lastMeta?.workLocation&&<p style={{margin:"1px 0 0",fontSize:10,color:wlocColor(s.lastMeta.workLocation),fontWeight:600}}>{wlocIcon(s.lastMeta.workLocation)} {s.lastMeta.workLocation}</p>}
                    {s.user.location&&<p style={{margin:"1px 0 0",fontSize:10,color:"#6b7280",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📍 {s.user.location}</p>}
                  </div>
                  <Donut pct={s.pct} color={pctColor(s.pct)}/>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6,marginBottom:s.avgDur?8:0}}>
                  {[{l:"Present",v:s.wdPresent,c:"#059669"},{l:"Tasks",v:s.total,c:"#2563eb"},{l:"Done",v:s.done,c:"#7c3aed"},{l:"Rate",v:s.compPct+"%",c:"#f59e0b"}].map(x=>(
                    <div key={x.l} style={{textAlign:"center",background:"#f8fafc",borderRadius:8,padding:"6px 3px"}}>
                      <p style={{margin:0,fontWeight:800,fontSize:15,color:x.c}}>{x.v}</p>
                      <p style={{margin:0,fontSize:9,color:"#9ca3af",fontWeight:600}}>{x.l}</p>
                    </div>
                  ))}
                </div>
                {/* Office vs WFH mini bar */}
                {(s.offDays>0||s.wfhDays>0)&&(
                  <div style={{marginTop:8}}>
                    <div style={{display:"flex",gap:4,alignItems:"center",marginBottom:3}}>
                      <span style={{fontSize:10,color:"#059669",fontWeight:600}}>🏢 {s.offDays}d</span>
                      <span style={{fontSize:10,color:"#9ca3af"}}>·</span>
                      <span style={{fontSize:10,color:"#7c3aed",fontWeight:600}}>🏠 {s.wfhDays}d</span>
                    </div>
                    <div style={{height:4,borderRadius:2,background:"#e5e7eb",overflow:"hidden",display:"flex"}}>
                      {s.offDays>0&&<div style={{flex:s.offDays,background:"#059669"}}/>}
                      {s.wfhDays>0&&<div style={{flex:s.wfhDays,background:"#7c3aed"}}/>}
                    </div>
                  </div>
                )}
                {s.avgDur&&<p style={{margin:"8px 0 0",fontSize:11,color:"#6b7280"}}>⏱ Avg task duration: <strong>{s.avgDur}</strong></p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ ATTENDANCE TABLE ══ */}
      {view==="attendance"&&(
        <div style={{display:"flex",flexDirection:"column",gap:20}}>
          {/* Full team table */}
          {card(<>
            {sectionTitle("All Employees Attendance",`${workdays.length} working days · ${dateFrom} to ${dateTo}`)}
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:900}}>
                <thead>
                  <tr style={{background:"#f8fafc"}}>
                    {["Member","Dept","Vertical","Last Work Location","Last Login","Days Present","Workday %","Office Days","WFH Days","Tasks","Completed","Status"].map(h=>(
                      <th key={h} style={{padding:"9px 11px",textAlign:"left",fontWeight:700,fontSize:11,color:"#374151",borderBottom:"2px solid #e5e7eb",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {userStats.map((s,i)=>{
                    const status=s.pct>=80?"🟢 Excellent":s.pct>=60?"🟡 Good":s.pct>=40?"🟠 Fair":"🔴 Poor";
                    const wl=s.lastMeta?.workLocation||"";
                    return(
                      <tr key={s.user.id}
                        style={{background:selUser===s.user.id?"#eff6ff":i%2===0?"#fff":"#fafafa",cursor:"pointer"}}
                        onClick={()=>setSelUser(selUser===s.user.id?"all":s.user.id)}
                        onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                        onMouseLeave={e=>e.currentTarget.style.background=selUser===s.user.id?"#eff6ff":i%2===0?"#fff":"#fafafa"}>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0"}}>
                          <div style={{display:"flex",alignItems:"center",gap:7}}>
                            <Avatar name={s.user.name} size={26}/>
                            <span style={{fontWeight:600,fontSize:12,color:"#111"}}>{s.user.name}</span>
                          </div>
                        </td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",fontSize:11,color:"#6b7280"}}>{s.user.department||"—"}</td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",fontSize:11,color:"#6b7280"}}>{s.user.vertical||"—"}</td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0"}}>
                          {wl?<span style={{fontSize:11,fontWeight:600,color:wlocColor(wl),background:wlocBg(wl),padding:"2px 8px",borderRadius:6}}>{wlocIcon(wl)} {wl}</span>:<span style={{color:"#d1d5db",fontSize:11}}>—</span>}
                        </td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",fontSize:11}}>
                          {s.lastMeta?.loginTime?<span style={{color:"#374151"}}>{fmtTime(s.lastMeta.loginTime)}</span>:<span style={{color:"#d1d5db"}}>—</span>}
                        </td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",fontSize:12,fontWeight:600,color:"#374151",textAlign:"center"}}>{s.wdPresent}/{workdays.length}</td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",textAlign:"center"}}>
                          <div style={{display:"inline-flex",alignItems:"center",gap:5}}>
                            <div style={{width:36,height:5,borderRadius:3,background:"#e5e7eb",overflow:"hidden"}}>
                              <div style={{width:`${s.pct}%`,height:"100%",background:pctColor(s.pct),borderRadius:3}}/>
                            </div>
                            <span style={{fontSize:12,fontWeight:700,color:pctColor(s.pct)}}>{s.pct}%</span>
                          </div>
                        </td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",fontSize:12,fontWeight:600,color:"#059669",textAlign:"center"}}>{s.offDays}</td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",fontSize:12,fontWeight:600,color:"#7c3aed",textAlign:"center"}}>{s.wfhDays}</td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",fontSize:12,fontWeight:600,color:"#2563eb",textAlign:"center"}}>{s.total}</td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",fontSize:12,fontWeight:600,color:"#059669",textAlign:"center"}}>{s.done}</td>
                        <td style={{padding:"10px 11px",borderBottom:"1px solid #f0f0f0",fontSize:11,whiteSpace:"nowrap"}}>{status}</td>
                      </tr>
                    );
                  })}
                  {/* Team totals */}
                  {(()=>{
                    const t2={wdPresent:0,total:0,done:0,offDays:0,wfhDays:0};
                    userStats.forEach(s=>{t2.wdPresent+=s.wdPresent;t2.total+=s.total;t2.done+=s.done;t2.offDays+=s.offDays;t2.wfhDays+=s.wfhDays;});
                    const avgA=userStats.length?Math.round(userStats.reduce((s,u)=>s+u.pct,0)/userStats.length):0;
                    return(
                      <tr style={{background:"#f0f9ff",fontWeight:700}}>
                        <td style={{padding:"10px 11px",fontWeight:800,fontSize:12,color:"#1e40af"}} colSpan={3}>TEAM TOTAL</td>
                        <td colSpan={2}/>
                        <td style={{padding:"10px 11px",fontSize:12,color:"#1e40af",textAlign:"center"}}>{t2.wdPresent}</td>
                        <td style={{padding:"10px 11px",textAlign:"center"}}><span style={{fontSize:12,fontWeight:800,color:pctColor(avgA)}}>{avgA}%</span></td>
                        <td style={{padding:"10px 11px",fontSize:12,color:"#059669",textAlign:"center"}}>{t2.offDays}</td>
                        <td style={{padding:"10px 11px",fontSize:12,color:"#7c3aed",textAlign:"center"}}>{t2.wfhDays}</td>
                        <td style={{padding:"10px 11px",fontSize:12,color:"#2563eb",textAlign:"center"}}>{t2.total}</td>
                        <td style={{padding:"10px 11px",fontSize:12,color:"#059669",textAlign:"center"}}>{t2.done}</td>
                        <td/>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            {userStats.length===0&&<p style={{textAlign:"center",padding:32,color:"#9ca3af"}}>No attendance data for this period</p>}
            <p style={{margin:"10px 0 0",fontSize:11,color:"#9ca3af"}}>💡 Click a row to view that employee's details below</p>
          </>)}

          {/* Selected employee detail */}
          {selUser!=="all"&&(()=>{
            const s=userStats.find(x=>x.user.id===selUser);
            if(!s) return null;
            // Day-by-day attendance log
            const dayLog=rangeDays.map(d=>{
              const meta=loginMeta[s.user.id]?.[d];
              const dayTasks=(tasksByUser[s.user.id]||[]).filter(t=>(t.date||"")=== d);
              return{date:d,meta,tasks:dayTasks};
            }).filter(r=>r.meta||r.tasks.length>0);
            return(
              <div style={{display:"flex",flexDirection:"column",gap:16}}>
                {/* Profile banner */}
                {card(
                  <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
                    <Avatar name={s.user.name} size={56}/>
                    <div style={{flex:1}}>
                      <h3 style={{margin:"0 0 2px",fontSize:17,fontWeight:800,color:"#0f172a"}}>{s.user.name}</h3>
                      {s.user.department&&<p style={{margin:"1px 0 0",fontSize:12,color:"#6b7280"}}>🏢 {s.user.department}{s.user.vertical?` · ⬆️ ${s.user.vertical}`:""}</p>}
                      {s.user.location&&<p style={{margin:"1px 0 0",fontSize:12,color:"#6b7280"}}>📍 {s.user.location}</p>}
                    </div>
                    <div style={{display:"flex",gap:12}}>
                      <div style={{textAlign:"center"}}>
                        <Donut pct={s.pct} color={pctColor(s.pct)} size={64}/>
                        <p style={{margin:"3px 0 0",fontSize:9,color:"#9ca3af",fontWeight:700}}>ATTENDANCE</p>
                      </div>
                      <div style={{textAlign:"center"}}>
                        <Donut pct={s.compPct} color="#7c3aed" size={64}/>
                        <p style={{margin:"3px 0 0",fontSize:9,color:"#9ca3af",fontWeight:700}}>COMPLETION</p>
                      </div>
                    </div>
                  </div>
                )}
                {/* Stats grid */}
                {card(
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8}}>
                    {[
                      {l:"Days Present", v:s.wdPresent,    c:"#059669"},
                      {l:"Working Days", v:workdays.length, c:"#374151"},
                      {l:"Office Days",  v:s.offDays,       c:"#059669"},
                      {l:"WFH Days",     v:s.wfhDays,       c:"#7c3aed"},
                      {l:"Tasks",        v:s.total,          c:"#2563eb"},
                      {l:"Completed",    v:s.done,           c:"#059669"},
                      {l:"In Progress",  v:s.inProg,         c:"#7c3aed"},
                      {l:"Avg Duration", v:s.avgDur||"—",   c:"#f59e0b"},
                    ].map(x=>(
                      <div key={x.l} style={{textAlign:"center",background:"#f8fafc",borderRadius:10,padding:"10px 6px",border:"1px solid #f0f0f0"}}>
                        <p style={{margin:0,fontWeight:800,fontSize:16,color:x.c}}>{x.v}</p>
                        <p style={{margin:0,fontSize:9,color:"#9ca3af",fontWeight:600,textTransform:"uppercase"}}>{x.l}</p>
                      </div>
                    ))}
                  </div>
                )}
                {/* Hourly engagement heatmap */}
                {card(<>
                  {sectionTitle("Hourly Engagement — Heatmap & Bar Chart","Activity count per hour of day")}
                  <HourlyHeatmap statOrNull={s}/>
                </>)}
                {/* Day-by-day log */}
                {card(<>
                  {sectionTitle("Daily Attendance Log",`${dayLog.length} active days in range`)}
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {dayLog.slice().reverse().map(r=>{
                      const wl=r.meta?.workLocation||"";
                      const src=r.meta?.sources||{};
                      return(
                        <div key={r.date} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",background:"#f8fafc",borderRadius:10,border:"1.5px solid #f0f0f0"}}>
                          <div style={{minWidth:80}}>
                            <p style={{margin:0,fontSize:12,fontWeight:700,color:"#374151"}}>{fmtDate(r.date)}</p>
                            <p style={{margin:0,fontSize:10,color:"#9ca3af"}}>{new Date(r.date+"T00:00:00").toLocaleDateString("en-US",{weekday:"short"})}</p>
                          </div>
                          <div style={{flex:1}}>
                            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:3}}>
                              {src.login&&<span style={{fontSize:10,fontWeight:700,background:"#f0fdf4",color:"#059669",padding:"1px 7px",borderRadius:4}}>🔑 Login{r.meta?.loginTime?" "+fmtTime(r.meta.loginTime):""}</span>}
                              {src.task&&<span style={{fontSize:10,fontWeight:700,background:"#eff6ff",color:"#2563eb",padding:"1px 7px",borderRadius:4}}>📋 Task activity</span>}
                              {wl&&<span style={{fontSize:10,fontWeight:700,background:wlocBg(wl),color:wlocColor(wl),padding:"1px 7px",borderRadius:4}}>{wlocIcon(wl)} {wl}</span>}
                              {r.meta?.location&&<span style={{fontSize:10,color:"#6b7280",padding:"1px 7px",borderRadius:4,background:"#f3f4f6"}}>📍 {r.meta.location}</span>}
                            </div>
                            {r.tasks.length>0&&(
                              <p style={{margin:0,fontSize:10,color:"#6b7280"}}>{r.tasks.length} task{r.tasks.length!==1?"s":""}: {r.tasks.map(t=>t.taskName).join(", ")}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {dayLog.length===0&&<p style={{color:"#9ca3af",padding:16,textAlign:"center"}}>No attendance records in this range</p>}
                  </div>
                </>)}
              </div>
            );
          })()}
        </div>
      )}

      {/* ══ CALENDAR ══ */}
      {view==="calendar"&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16}}>
          {visStats.map(s=>(
            <div key={s.user.id} style={{background:"#fff",borderRadius:16,padding:"20px",border:"1.5px solid #f0f0f0",boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <Avatar name={s.user.name} size={36}/>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontWeight:700,fontSize:14,color:"#111"}}>{s.user.name}</p>
                  <p style={{margin:"1px 0 0",fontSize:11,color:"#9ca3af"}}>{s.wdPresent}/{workdays.length} working days · {s.pct}%</p>
                  {s.lastMeta?.workLocation&&<p style={{margin:"1px 0 0",fontSize:10,color:wlocColor(s.lastMeta.workLocation),fontWeight:600}}>{wlocIcon(s.lastMeta.workLocation)} {s.lastMeta.workLocation}</p>}
                </div>
                <div style={{background:pctBg(s.pct),borderRadius:8,padding:"3px 10px"}}>
                  <span style={{fontSize:13,fontWeight:800,color:pctColor(s.pct)}}>{s.pct}%</span>
                </div>
              </div>
              <p style={{margin:"0 0 8px",fontSize:11,fontWeight:700,color:"#6b7280",textTransform:"uppercase",letterSpacing:"0.05em"}}>{MONTH_NAMES[calMonth-1]} {calYear}</p>
              <UserCalendar stat={s}/>
              <div style={{display:"flex",gap:6,marginTop:10,flexWrap:"wrap"}}>
                {[{l:"Office",v:s.offDays,c:"#059669"},{l:"WFH",v:s.wfhDays,c:"#7c3aed"},{l:"Tasks",v:s.total,c:"#2563eb"},{l:"Done",v:s.done,c:"#059669"}].map(x=>(
                  <div key={x.l} style={{flex:"1 1 60px",textAlign:"center",background:"#f8fafc",borderRadius:8,padding:"6px 3px"}}>
                    <p style={{margin:0,fontWeight:800,fontSize:14,color:x.c}}>{x.v}</p>
                    <p style={{margin:0,fontSize:9,color:"#9ca3af",fontWeight:600,textTransform:"uppercase"}}>{x.l}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {visStats.length===0&&<p style={{color:"#9ca3af",padding:40,textAlign:"center",gridColumn:"1/-1"}}>No data found</p>}
        </div>
      )}

      {/* ══ TABULATION ══ */}
      {view==="table"&&(
        <div style={{background:"#fff",borderRadius:16,border:"1.5px solid #e5e7eb",overflow:"hidden",boxShadow:"0 2px 12px rgba(0,0,0,.05)"}}>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:950}}>
              <thead>
                <tr style={{background:"#f8fafc"}}>
                  {["Member","Dept","Vertical","Location","Work Location","Last Login","Workday Present","Attendance %","Tasks","Completed","Completion %","Avg Duration","Status"].map(h=>(
                    <th key={h} style={{padding:"10px 12px",textAlign:"left",fontWeight:700,fontSize:11,color:"#374151",borderBottom:"2px solid #e5e7eb",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visStats.map((s,i)=>{
                  const status=s.pct>=80?"🟢 Excellent":s.pct>=60?"🟡 Good":s.pct>=40?"🟠 Fair":"🔴 Poor";
                  const wl=s.lastMeta?.workLocation||"";
                  const userRecs=attendance.filter(r=>r.userId===s.user.id&&r.date>=dateFrom&&r.date<=dateTo).sort((a,b)=>(b.date||"").localeCompare(a.date||""));
                  const lastRec=userRecs[0];
                  return(
                    <tr key={s.user.id} style={{background:i%2===0?"#fff":"#fafafa"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                      onMouseLeave={e=>e.currentTarget.style.background=i%2===0?"#fff":"#fafafa"}>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <Avatar name={s.user.name} size={26}/>
                          <span style={{fontWeight:600,fontSize:12,color:"#111"}}>{s.user.name}</span>
                        </div>
                      </td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",fontSize:11,color:"#6b7280"}}>{s.user.department||"—"}</td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",fontSize:11,color:"#6b7280"}}>{s.user.vertical||"—"}</td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",fontSize:11,color:"#6b7280",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {s.user.location?<>📍 {s.user.location}</>:"—"}
                      </td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0"}}>
                        {wl?<span style={{fontSize:11,fontWeight:600,color:wlocColor(wl),background:wlocBg(wl),padding:"2px 8px",borderRadius:6}}>{wlocIcon(wl)} {wl}</span>:<span style={{color:"#d1d5db",fontSize:11}}>—</span>}
                      </td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",fontSize:11,minWidth:130}}>
                        {lastRec?(
                          <div>
                            <div style={{display:"flex",gap:4,marginBottom:2}}>
                              {lastRec.sources?.login&&<span style={{fontSize:9,fontWeight:700,background:"#f0fdf4",color:"#059669",padding:"1px 6px",borderRadius:4}}>🔑</span>}
                              {lastRec.sources?.task&&<span style={{fontSize:9,fontWeight:700,background:"#eff6ff",color:"#2563eb",padding:"1px 6px",borderRadius:4}}>📋</span>}
                            </div>
                            {lastRec.loginTime&&<div style={{fontWeight:600,color:"#374151"}}>{fmtTime(lastRec.loginTime)}</div>}
                          </div>
                        ):<span style={{color:"#d1d5db"}}>—</span>}
                      </td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",fontSize:12,fontWeight:600,color:"#374151",textAlign:"center"}}>{s.wdPresent} / {workdays.length}</td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",textAlign:"center"}}>
                        <div style={{display:"inline-flex",alignItems:"center",gap:5}}>
                          <div style={{width:40,height:5,borderRadius:3,background:"#e5e7eb",overflow:"hidden"}}>
                            <div style={{width:`${s.pct}%`,height:"100%",background:pctColor(s.pct),borderRadius:3}}/>
                          </div>
                          <span style={{fontSize:12,fontWeight:700,color:pctColor(s.pct)}}>{s.pct}%</span>
                        </div>
                      </td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",fontSize:12,fontWeight:600,color:"#2563eb",textAlign:"center"}}>{s.total}</td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",fontSize:12,fontWeight:600,color:"#059669",textAlign:"center"}}>{s.done}</td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",textAlign:"center"}}>
                        <div style={{display:"inline-flex",alignItems:"center",gap:5}}>
                          <div style={{width:40,height:5,borderRadius:3,background:"#e5e7eb",overflow:"hidden"}}>
                            <div style={{width:`${s.compPct}%`,height:"100%",background:"#7c3aed",borderRadius:3}}/>
                          </div>
                          <span style={{fontSize:12,fontWeight:700,color:"#7c3aed"}}>{s.compPct}%</span>
                        </div>
                      </td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",fontSize:11,color:"#6b7280",textAlign:"center"}}>{s.avgDur||"—"}</td>
                      <td style={{padding:"10px 12px",borderBottom:"1px solid #f0f0f0",fontSize:11,whiteSpace:"nowrap"}}>{status}</td>
                    </tr>
                  );
                })}
                {selUser==="all"&&visStats.length>0&&(()=>{
                  const t2={wdPresent:0,total:0,done:0};
                  visStats.forEach(s=>{t2.wdPresent+=s.wdPresent;t2.total+=s.total;t2.done+=s.done;});
                  const avgA=Math.round(visStats.reduce((s,u)=>s+u.pct,0)/visStats.length);
                  const tc=t2.total?Math.round(t2.done/t2.total*100):0;
                  return(<tr style={{background:"#f0f9ff",fontWeight:700}}>
                    <td style={{padding:"10px 12px",fontSize:12,color:"#1e40af",fontWeight:800}} colSpan={6}>TEAM TOTAL</td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"#1e40af",textAlign:"center"}}>{t2.wdPresent}</td>
                    <td style={{padding:"10px 12px",textAlign:"center"}}><span style={{fontSize:12,fontWeight:800,color:pctColor(avgA)}}>{avgA}%</span></td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"#1e40af",textAlign:"center"}}>{t2.total}</td>
                    <td style={{padding:"10px 12px",fontSize:12,color:"#059669",textAlign:"center"}}>{t2.done}</td>
                    <td style={{padding:"10px 12px",textAlign:"center"}}><span style={{fontSize:12,fontWeight:800,color:"#7c3aed"}}>{tc}%</span></td>
                    <td colSpan={2}/>
                  </tr>);
                })()}
              </tbody>
            </table>
          </div>
          {visStats.length===0&&<p style={{textAlign:"center",padding:40,color:"#9ca3af"}}>No data for this period</p>}
        </div>
      )}

      {/* ══ PERFORMANCE / CHART ══ */}
      {view==="chart"&&(
        <div style={{display:"flex",flexDirection:"column",gap:20}}>

          {/* Individual member */}
          {selUser!=="all"&&visStats.length>0&&(()=>{
            const s=visStats[0];
            const dc=rangeDays.map(d=>(tasksByUser[s.user.id]||[]).filter(t=>(t.date||"")===d).length);
            const mx=Math.max(1,...dc);
            return(<div style={{display:"flex",flexDirection:"column",gap:16}}>
              {card(<>
                <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
                  <Avatar name={s.user.name} size={64}/>
                  <div style={{flex:1}}>
                    <h3 style={{margin:"0 0 3px",fontSize:18,fontWeight:800,color:"#0f172a"}}>{s.user.name}</h3>
                    <p style={{margin:0,fontSize:12,color:"#9ca3af"}}>{s.user.role==="admin"?"🔐 Administrator":"👤 Team Member"}</p>
                    {s.user.department&&<p style={{margin:"2px 0 0",fontSize:12,color:"#6b7280"}}>🏢 {s.user.department}</p>}
                    {s.user.vertical&&<p style={{margin:"2px 0 0",fontSize:12,color:"#6b7280"}}>⬆️ {s.user.vertical}</p>}
                    {s.lastMeta?.workLocation&&<p style={{margin:"2px 0 0",fontSize:12,color:wlocColor(s.lastMeta.workLocation),fontWeight:600}}>{wlocIcon(s.lastMeta.workLocation)} Last: {s.lastMeta.workLocation}</p>}
                    {s.user.location&&<p style={{margin:"2px 0 0",fontSize:12,color:"#6b7280"}}>📍 {s.user.location}</p>}
                  </div>
                  <div style={{display:"flex",gap:16}}>
                    <div style={{textAlign:"center"}}><Donut pct={s.pct} color={pctColor(s.pct)} size={72}/><p style={{margin:"4px 0 0",fontSize:10,color:"#9ca3af",fontWeight:700}}>ATTENDANCE</p></div>
                    <div style={{textAlign:"center"}}><Donut pct={s.compPct} color="#7c3aed" size={72}/><p style={{margin:"4px 0 0",fontSize:10,color:"#9ca3af",fontWeight:700}}>COMPLETION</p></div>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))",gap:8,marginTop:16}}>
                  {[
                    {l:"Days Present",v:s.wdPresent,       c:"#059669"},
                    {l:"Working Days",v:workdays.length,    c:"#374151"},
                    {l:"Office Days", v:s.offDays,          c:"#059669"},
                    {l:"WFH Days",    v:s.wfhDays,          c:"#7c3aed"},
                    {l:"Tasks Total", v:s.total,             c:"#2563eb"},
                    {l:"Completed",   v:s.done,              c:"#059669"},
                    {l:"In Progress", v:s.inProg,            c:"#7c3aed"},
                    {l:"Avg Duration",v:s.avgDur||"—",      c:"#f59e0b"},
                  ].map(x=>(
                    <div key={x.l} style={{textAlign:"center",background:"#f8fafc",borderRadius:10,padding:"10px 6px",border:"1px solid #f0f0f0"}}>
                      <p style={{margin:0,fontWeight:800,fontSize:16,color:x.c}}>{x.v}</p>
                      <p style={{margin:0,fontSize:9,color:"#9ca3af",fontWeight:600,textTransform:"uppercase"}}>{x.l}</p>
                    </div>
                  ))}
                </div>
              </>)}
              {card(<>
                {sectionTitle("Daily Task Activity",`${dateFrom} → ${dateTo}`)}
                <div style={{display:"flex",gap:1,alignItems:"flex-end",height:48,marginBottom:4}}>
                  {dc.map((n,i)=>(<div key={i} title={`${rangeDays[i]}: ${n} tasks`} style={{flex:1,height:`${Math.max(n?20:4,Math.round(n/mx*48))}px`,background:n>0?heatColor(n):"#e5e7eb",borderRadius:2,minWidth:2,cursor:"default"}}/>))}
                </div>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                  <span style={{fontSize:10,color:"#9ca3af"}}>{dateFrom}</span>
                  <span style={{fontSize:10,color:"#9ca3af"}}>{dateTo}</span>
                </div>
              </>)}
              {card(<>
                {sectionTitle("Hourly Engagement — Heatmap & Bar Chart","Activity count per hour of day (login events + task start/end times)")}
                <HourlyHeatmap statOrNull={s}/>
              </>)}
              {s.total>0&&card(<>
                {sectionTitle("Task Status Breakdown")}
                {[
                  {l:"Completed",   v:s.done,   c:"#059669",bg:"#f0fdf4"},
                  {l:"In Progress", v:s.inProg,  c:"#7c3aed",bg:"#f5f3ff"},
                  {l:"Review",      v:s.review,  c:"#2563eb",bg:"#eff6ff"},
                  {l:"Draft",       v:s.draft,   c:"#9ca3af",bg:"#f9fafb"},
                ].filter(x=>x.v>0).map(x=>(
                  <div key={x.l} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                      <span style={{fontSize:12,fontWeight:600,color:x.c}}>{x.l}</span>
                      <span style={{fontSize:12,color:"#6b7280"}}>{x.v} ({Math.round(x.v/s.total*100)}%)</span>
                    </div>
                    <div style={{height:8,borderRadius:4,background:"#e5e7eb",overflow:"hidden"}}>
                      <div style={{width:`${Math.round(x.v/s.total*100)}%`,height:"100%",background:x.c,borderRadius:4}}/>
                    </div>
                  </div>
                ))}
              </>)}
            </div>);
          })()}

          {/* All members */}
          {selUser==="all"&&(<>
            {card(<>
              {sectionTitle("Attendance Rate — All Members","Workday presence percentage")}
              <div style={{overflowX:"auto",paddingBottom:4}}>
                <BarChart data={[...userStats].sort((a,b)=>b.pct-a.pct).map(s=>({label:s.user.name,val:s.pct,suffix:"%",color:pctColor(s.pct)}))} maxVal={100} h={150}/>
              </div>
            </>)}
            {card(<>
              {sectionTitle("Tasks Submitted vs Completed")}
              <div style={{overflowX:"auto",paddingBottom:4,display:"flex",gap:24,alignItems:"flex-end"}}>
                <div>
                  <p style={{margin:"0 0 4px",fontSize:10,fontWeight:700,color:"#2563eb",textTransform:"uppercase"}}>📋 Submitted</p>
                  <BarChart data={[...userStats].sort((a,b)=>b.total-a.total).map(s=>({label:s.user.name,val:s.total,color:"#3b82f6"}))} maxVal={Math.max(1,...userStats.map(s=>s.total))} h={130}/>
                </div>
                <div>
                  <p style={{margin:"0 0 4px",fontSize:10,fontWeight:700,color:"#059669",textTransform:"uppercase"}}>✅ Completed</p>
                  <BarChart data={[...userStats].sort((a,b)=>b.total-a.total).map(s=>({label:s.user.name,val:s.done,color:"#10b981"}))} maxVal={Math.max(1,...userStats.map(s=>s.total))} h={130}/>
                </div>
              </div>
            </>)}
            {card(<>
              {sectionTitle("Team Hourly Engagement — Heatmap & Bar Chart","Combined activity by hour across all members")}
              <HourlyHeatmap statOrNull={null}/>
            </>)}
            {card(<>
              {sectionTitle("Team Activity Calendar Heatmap","Daily total tasks")}
              <div style={{overflowX:"auto"}}>
                <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,minWidth:240}}>
                  {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=>(
                    <div key={d} style={{textAlign:"center",fontSize:10,color:"#9ca3af",fontWeight:700,padding:"2px 0"}}>{d}</div>
                  ))}
                  {Array.from({length:firstWd},(_,i)=><div key={`e${i}`}/>)}
                  {calDays.map(d=>{
                    const n=tasks.filter(t=>(t.date||"")===d&&t.status!=="Draft").length;
                    const inRange=d>=dateFrom&&d<=dateTo;
                    return(<div key={d} title={`${d}: ${n} task${n!==1?"s":""}`} style={{
                      aspectRatio:"1",borderRadius:5,background:!inRange?"#f9fafb":heatColor(n),
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:10,color:n>3?"#fff":"#374151",fontWeight:600,
                      border:d===todayStr?"2px solid #f59e0b":"none",opacity:!inRange?0.3:1
                    }}>
                      {new Date(d+"T00:00:00").getDate()}
                    </div>);
                  })}
                </div>
              </div>
            </>)}
            {card(<>
              {sectionTitle("Individual Performance Cards")}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:12}}>
                {userStats.map(s=>{
                  const dc=rangeDays.map(d=>(tasksByUser[s.user.id]||[]).filter(t=>(t.date||"")===d).length);
                  const mx=Math.max(1,...dc);
                  const wl=s.lastMeta?.workLocation||"";
                  return(<div key={s.user.id} style={{border:"1.5px solid #f0f0f0",borderRadius:12,padding:"14px",background:"#fafafa"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                      <Avatar name={s.user.name} size={32}/>
                      <div style={{flex:1,minWidth:0}}>
                        <p style={{margin:0,fontSize:12,fontWeight:700,color:"#111",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.user.name}</p>
                        {s.user.department&&<p style={{margin:0,fontSize:10,color:"#9ca3af"}}>{s.user.department}{s.user.vertical?` · ${s.user.vertical}`:""}</p>}
                        {wl&&<p style={{margin:0,fontSize:10,color:wlocColor(wl),fontWeight:600}}>{wlocIcon(wl)} {wl}</p>}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:8,justifyContent:"center",marginBottom:8}}>
                      <div style={{textAlign:"center"}}><Donut pct={s.pct} color={pctColor(s.pct)} size={52}/><p style={{margin:"2px 0 0",fontSize:9,color:"#9ca3af",fontWeight:600}}>ATTEND.</p></div>
                      <div style={{textAlign:"center"}}><Donut pct={s.compPct} color="#7c3aed" size={52}/><p style={{margin:"2px 0 0",fontSize:9,color:"#9ca3af",fontWeight:600}}>COMPLET.</p></div>
                    </div>
                    <div style={{display:"flex",gap:1,alignItems:"flex-end",height:20,marginBottom:6}}>
                      {dc.map((n,i)=>(<div key={i} style={{flex:1,height:`${Math.max(n?30:10,Math.round(n/mx*100))}%`,background:n>0?"#6366f1":"#e5e7eb",borderRadius:1,minWidth:1}}/>))}
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:4}}>
                      {[{l:"Days",v:s.wdPresent,c:"#059669"},{l:"Tasks",v:s.total,c:"#2563eb"},{l:"Done",v:s.done,c:"#7c3aed"}].map(x=>(
                        <div key={x.l} style={{textAlign:"center",background:"#fff",borderRadius:6,padding:"5px 2px",border:"1px solid #f0f0f0"}}>
                          <p style={{margin:0,fontWeight:800,fontSize:13,color:x.c}}>{x.v}</p>
                          <p style={{margin:0,fontSize:8,color:"#9ca3af",fontWeight:600}}>{x.l}</p>
                        </div>
                      ))}
                    </div>
                  </div>);
                })}
              </div>
            </>)}
            {deptStats.some(g=>g.name!=="Unassigned"||g.total>0)&&card(<>
              {sectionTitle("Department Performance")}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
                {deptStats.map(g=><GroupCard key={g.name} g={g} icon="🏢"/>)}
              </div>
              {deptStats.length>1&&<div style={{marginTop:14,overflowX:"auto"}}><BarChart data={deptStats.map(g=>({label:g.name,val:g.pct,suffix:"%",color:pctColor(g.pct)}))} maxVal={100} h={120}/></div>}
            </>)}
            {verticalStats.some(g=>g.name!=="Unassigned"||g.total>0)&&card(<>
              {sectionTitle("Vertical / Team Performance")}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
                {verticalStats.map(g=><GroupCard key={g.name} g={g} icon="⬆️"/>)}
              </div>
              {verticalStats.length>1&&<div style={{marginTop:14,overflowX:"auto"}}><BarChart data={verticalStats.map(g=>({label:g.name,val:g.pct,suffix:"%",color:pctColor(g.pct)}))} maxVal={100} h={120}/></div>}
            </>)}
            {deptStats.every(g=>g.name==="Unassigned")&&verticalStats.every(g=>g.name==="Unassigned")&&(
              <div style={{background:"#fffbeb",border:"1.5px solid #fde68a",borderRadius:14,padding:"20px 24px",textAlign:"center"}}>
                <p style={{margin:0,fontSize:14,fontWeight:700,color:"#92400e"}}>⚠️ No Department or Vertical data set</p>
                <p style={{margin:"6px 0 0",fontSize:12,color:"#b45309"}}>Ask team members to update their profile to unlock group analytics.</p>
              </div>
            )}
          </>)}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   AUTO TASK GENERATION ENGINE
   Monitors browser tab context, detects activity type,
   deduplicates, calls Claude API to generate task drafts,
   and prompts the user to create or append.
───────────────────────────────────────────────────────────── */

// Fingerprint the current browser context

/* ─────────────────────────────────────────────────────────────
   AI TASK CAPTURE SYSTEM
   A floating "AI Capture" button visible at all times.
   User describes what they're working on → Claude generates
   a structured task → user confirms, edits, or appends.
   Works 100% inside the browser without needing OS access.
───────────────────────────────────────────────────────────── */

// Smart local task generator — works 100% in browser, no API/CORS issues
const generateTaskFromDescription = async (userInput, existingTasks) => {
  const input = userInput.trim();
  const lower = input.toLowerCase();

  const isMeeting  = /\b(zoom|meet|teams|call|meeting|webex|conference|standup|sync|discussion|session)\b/.test(lower);
  const isReview   = /\b(review|feedback|check|approve|audit|inspect|evaluate|assess)\b/.test(lower);
  const isDesign   = /\b(figma|design|ui|ux|mockup|wireframe|prototype|canva|photoshop|sketch)\b/.test(lower);
  const isDev      = /\b(code|bug|fix|github|deploy|develop|build|api|feature|issue|pr|pull request|commit)\b/.test(lower);
  const isDoc      = /\b(document|report|write|draft|proposal|email|content|copy|article|blog|presentation)\b/.test(lower);
  const isResearch = /\b(research|analyse|analyze|investigate|study|look into|find out)\b/.test(lower);

  // Build task name
  let taskName = "";
  if (isMeeting) {
    const about = input.replace(/\b(zoom|teams|google meet|on a call|meeting|call about|call with|meeting about|meeting with|meeting on|sync on|sync about|standup|discussion about)\b/gi, "").replace(/\s+/g, " ").trim();
    taskName = ("Meeting: " + about).slice(0, 65);
  } else if (isReview) {
    taskName = ("Review: " + input.replace(/\b(review|give feedback on|check|reviewing)\b/gi, "").replace(/\s+/g, " ").trim()).slice(0, 65);
  } else if (isDev) {
    taskName = ("Dev: " + input.replace(/\b(working on|coding|fixing|implementing|building)\b/gi, "").replace(/\s+/g, " ").trim()).slice(0, 65);
  } else if (isDesign) {
    taskName = ("Design: " + input.replace(/\b(designing|working on|creating)\b/gi, "").replace(/\s+/g, " ").trim()).slice(0, 65);
  } else if (isDoc) {
    taskName = ("Document: " + input.replace(/\b(writing|drafting|creating|preparing)\b/gi, "").replace(/\s+/g, " ").trim()).slice(0, 65);
  } else {
    const stopWords = new Set(["a","an","the","and","or","but","in","on","at","to","for","of","with","is","am","are","i","my","we","our"]);
    const sigWords  = input.split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w.toLowerCase())).slice(0, 7);
    taskName = sigWords.join(" ");
    taskName = taskName.charAt(0).toUpperCase() + taskName.slice(1);
  }
  taskName = taskName.replace(/[.!?,;:]+$/, "").replace(/\s+/g, " ").trim();

  // Build description
  const timeStr = new Date().toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true });
  let description = input.charAt(0).toUpperCase() + input.slice(1);
  if (!description.endsWith(".")) description += ".";
  description += " Captured at " + timeStr + ".";
  if (isMeeting)  description += " Document key decisions and action items from this meeting.";
  if (isReview)   description += " Record findings, feedback, and any required follow-up.";
  if (isDev)      description += " Track progress, blockers, and testing steps.";
  if (isDoc)      description += " Include review cycle and sign-off requirements.";

  // Detect priority
  let priority = "Medium";
  if (/\b(urgent|asap|critical|emergency|high priority|immediately|deadline today|due today|blocking)\b/.test(lower)) priority = "High";
  else if (/\b(whenever|low priority|not urgent|no rush|eventually|someday)\b/.test(lower)) priority = "Low";
  else if (isMeeting || isDev) priority = "High";

  // Check if should append to existing task (keyword overlap)
  let shouldAppend = false, appendReason = "", appendTargetId = null;
  const activeTasks = existingTasks.filter(t => t.status !== "Completed" && t.status !== "Draft");
  for (const t of activeTasks) {
    const taskText  = ((t.taskName || "") + " " + (t.description || "")).toLowerCase();
    const inputKw   = lower.split(/\W+/).filter(w => w.length > 3);
    const taskKw    = taskText.split(/\W+/).filter(w => w.length > 3);
    const overlap   = inputKw.filter(w => taskKw.includes(w));
    if (overlap.length >= 2) {
      shouldAppend   = true;
      appendReason   = "This activity seems related to your existing task \"" + t.taskName + "\" based on matching keywords.";
      appendTargetId = t.id;
      break;
    }
  }

  // Suggest project from keywords
  let suggestedProject = "";
  const projectHints = [
    { words:["website","web","landing","homepage"],        project:"Website" },
    { words:["marketing","campaign","ads","social"],       project:"Marketing" },
    { words:["admin","dashboard","portal","backend"],      project:"Admin" },
    { words:["design","ui","ux","figma","brand"],         project:"Design" },
    { words:["app","mobile","ios","android","flutter"],   project:"App Development" },
    { words:["report","analysis","data","analytics"],     project:"Analytics" },
    { words:["hr","hiring","onboarding","recruitment"],   project:"HR" },
  ];
  for (const hint of projectHints) {
    if (hint.words.some(w => lower.includes(w))) { suggestedProject = hint.project; break; }
  }

  return { taskName, description, priority, suggestedProject, shouldAppend, appendReason, appendTargetId };
};

// Enrich existing task description locally — no API call needed
const enrichExistingTask = async (existingTask, userInput) => {
  const existing   = existingTask.description || "";
  const timeStr    = new Date().toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit", hour12:true });
  const newContext = userInput.trim().charAt(0).toUpperCase() + userInput.trim().slice(1);
  const updatedDescription = existing
    ? existing + "\n\n[Update \u2013 " + timeStr + "] " + newContext + (newContext.endsWith(".") ? "" : ".")
    : "[" + timeStr + "] " + newContext + (newContext.endsWith(".") ? "" : ".");
  return { updatedDescription };
};

/* ── AI Capture Panel ── */
function AICapturePanel({ tasks, users, projects, currentUser, prefill, onSaveTask, onUpdateTask, onClose }) {
  const [step,        setStep]        = useState(prefill ? "review" : "input");
  const [userInput,   setUserInput]   = useState(prefill ? (prefill.description || "") : "");
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [generated,   setGenerated]   = useState(prefill || null);
  const [editedTask,  setEditedTask]  = useState(prefill ? {
    taskName:    prefill.taskName    || "",
    description: prefill.description || "",
    priority:    prefill.priority    || "Medium",
  } : null);
  const [selProject,  setSelProject]  = useState("");
  const [selAssign,   setSelAssign]   = useState(currentUser.name);
  const [selTask,     setSelTask]     = useState("");
  const [saving,      setSaving]      = useState(false);

  const myActiveTasks = tasks.filter(t =>
    (t.assignedTo === currentUser.name || t.createdBy === currentUser.id) &&
    t.status !== "Completed"
  );

  const handleGenerate = async () => {
    if (!userInput.trim()) return;
    setLoading(true); setError("");
    try {
      const result = await generateTaskFromDescription(userInput.trim(), myActiveTasks);
      setGenerated(result);
      setEditedTask({
        taskName:    result.taskName,
        description: result.description,
        priority:    result.priority,
      });
      setSelProject(result.suggestedProject || "");
      // Pre-select an existing task if AI suggests appending
      if (result.shouldAppend && myActiveTasks.length > 0) {
        setSelTask(result.appendTargetId || myActiveTasks[0].id);
        setStep("select");
      } else {
        setStep("review");
      }
    } catch (e) {
      setError("AI generation failed. Check your internet connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    const now = nowISO();
    const t = {
      id:             `task-${uid()}`,
      taskId:         `TASK-${String(Date.now()).slice(-6)}`,
      taskName:       editedTask.taskName,
      description:    editedTask.description,
      projectName:    selProject,
      assignedTo:     selAssign,
      priority:       editedTask.priority,
      status:         "Draft",
      date:           today(),
      createdBy:      currentUser.id,
      createdByName:  currentUser.name,
      startTime:      now,
      createdAt:      now,
      endTime:        null,
      pausedDuration: 0,
      draftDuration:  0,
      draftStartTime: now,
      holdStartTime:  null,
      subAssigned:    [],
      attachments:    [],
      aiGenerated:    true,
      aiSourceText:   userInput.slice(0, 200),
    };
    await onSaveTask(t);
    setSaving(false);
    setStep("done");
    setTimeout(onClose, 1500);
  };

  const handleAppend = async () => {
    const target = tasks.find(t => t.id === selTask);
    if (!target) return;
    setSaving(true);
    try {
      const result = await enrichExistingTask(target, userInput.trim());
      await onUpdateTask(target.id, { ...target, description: result.updatedDescription });
    } catch (e) {
      // fallback: just append raw text
      await onUpdateTask(target.id, {
        ...target,
        description: (target.description ? target.description + "\n\n" : "") + `[Update] ${userInput.trim()}`
      });
    }
    setSaving(false);
    setStep("done");
    setTimeout(onClose, 1500);
  };

  const setEdit = (k, v) => setEditedTask(p => ({ ...p, [k]: v }));

  const overlay = {
    position:"fixed", inset:0, background:"rgba(0,0,0,.5)",
    zIndex:10000, display:"flex", alignItems:"center", justifyContent:"center", padding:16
  };
  const modal = {
    background:"#fff", borderRadius:20, width:"100%", maxWidth:500,
    boxShadow:"0 24px 60px rgba(0,0,0,.2)", overflow:"hidden", maxHeight:"90vh", overflowY:"auto"
  };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        {/* Header */}
        <div style={{ background:"linear-gradient(135deg,#1e40af,#7c3aed)", padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>🤖</span>
            <div>
              <p style={{ margin:0, color:"#fff", fontWeight:800, fontSize:15 }}>AI Task Capture</p>
              <p style={{ margin:0, color:"rgba(255,255,255,.6)", fontSize:11 }}>
                {step==="input" && "Describe what you're working on"}
                {step==="review" && "Review & edit the generated task"}
                {step==="select" && "Choose: create new or append to existing"}
                {step==="done"  && "Task saved successfully!"}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"rgba(255,255,255,.15)", border:"none", borderRadius:8, padding:"5px 8px", cursor:"pointer", color:"#fff", fontSize:16 }}>✕</button>
        </div>

        <div style={{ padding:"20px 22px" }}>

          {/* ── STEP: INPUT ── */}
          {step === "input" && (
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.06em" }}>
                What are you working on right now?
              </label>
              <textarea
                autoFocus
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) handleGenerate(); }}
                placeholder={"Examples:\n• Reviewing the Q3 marketing report in Google Docs\n• On a Zoom call with the design team about the new website\n• Fixing a bug in the checkout flow on GitHub\n• Updating product listings on the admin dashboard"}
                style={{ width:"100%", minHeight:120, padding:"12px 14px", borderRadius:10, border:"1.5px solid #e5e7eb", fontSize:13, resize:"vertical", fontFamily:"inherit", outline:"none", color:"#374151", boxSizing:"border-box", lineHeight:1.6 }}
              />
              <p style={{ margin:"6px 0 0", fontSize:11, color:"#9ca3af" }}>Press Ctrl+Enter or click Generate. Be specific — better input = better task.</p>
              {error && <p style={{ margin:"8px 0 0", fontSize:12, color:"#dc2626", background:"#fef2f2", padding:"8px 12px", borderRadius:8 }}>{error}</p>}
              <div style={{ display:"flex", gap:8, marginTop:14, justifyContent:"flex-end" }}>
                <button onClick={onClose} style={{ padding:"9px 18px", borderRadius:10, border:"1.5px solid #e5e7eb", background:"#fff", color:"#374151", fontSize:13, cursor:"pointer" }}>Cancel</button>
                <button onClick={handleGenerate} disabled={!userInput.trim() || loading}
                  style={{ padding:"9px 22px", borderRadius:10, border:"none", background: userInput.trim()?"linear-gradient(135deg,#1e40af,#7c3aed)":"#e5e7eb", color:"#fff", fontWeight:700, fontSize:13, cursor: userInput.trim()?"pointer":"not-allowed", display:"flex", alignItems:"center", gap:8 }}>
                  {loading ? <>
                    <span style={{ width:14, height:14, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"spin .6s linear infinite" }}/>
                    Generating...
                  </> : "🤖 Generate Task"}
                </button>
              </div>
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            </div>
          )}

          {/* ── STEP: REVIEW ── */}
          {step === "review" && editedTask && (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {generated?.shouldAppend && (
                <div style={{ background:"#fffbeb", border:"1.5px solid #fde68a", borderRadius:10, padding:"10px 14px", fontSize:12, color:"#92400e" }}>
                  💡 {generated.appendReason} — <button onClick={() => setStep("select")} style={{ background:"none", border:"none", color:"#b45309", fontWeight:700, cursor:"pointer", fontSize:12, textDecoration:"underline" }}>Append to existing task instead?</button>
                </div>
              )}

              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Task Name</label>
                <input value={editedTask.taskName} onChange={e => setEdit("taskName", e.target.value)}
                  style={{ width:"100%", padding:"10px 12px", borderRadius:9, border:"1.5px solid #e5e7eb", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box", color:"#111" }}/>
              </div>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Description</label>
                <textarea value={editedTask.description} onChange={e => setEdit("description", e.target.value)}
                  style={{ width:"100%", minHeight:80, padding:"10px 12px", borderRadius:9, border:"1.5px solid #e5e7eb", fontSize:13, resize:"vertical", fontFamily:"inherit", outline:"none", boxSizing:"border-box", color:"#111", lineHeight:1.5 }}/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Priority</label>
                  <select value={editedTask.priority} onChange={e => setEdit("priority", e.target.value)}
                    style={{ width:"100%", padding:"9px 10px", borderRadius:9, border:"1.5px solid #e5e7eb", fontSize:13, background:"#fafafa", cursor:"pointer" }}>
                    {["High","Medium","Low"].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Project</label>
                  <select value={selProject} onChange={e => setSelProject(e.target.value)}
                    style={{ width:"100%", padding:"9px 10px", borderRadius:9, border:"1.5px solid #e5e7eb", fontSize:13, background:"#fafafa", cursor:"pointer" }}>
                    <option value="">None</option>
                    {[...projects].sort((a,b) => a.name.localeCompare(b.name)).map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#6b7280", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.06em" }}>Assign To</label>
                  <select value={selAssign} onChange={e => setSelAssign(e.target.value)}
                    style={{ width:"100%", padding:"9px 10px", borderRadius:9, border:"1.5px solid #e5e7eb", fontSize:13, background:"#fafafa", cursor:"pointer" }}>
                    {[...users].sort((a,b) => a.name.localeCompare(b.name)).map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ background:"#f0fdf4", borderRadius:9, padding:"9px 12px", display:"flex", gap:6, alignItems:"center" }}>
                <span style={{ fontSize:13 }}>📋</span>
                <span style={{ fontSize:12, color:"#059669", fontWeight:600 }}>Will be saved as Draft — you can edit and activate it from the task list</span>
              </div>
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:4 }}>
                <button onClick={() => setStep("input")} style={{ padding:"9px 16px", borderRadius:10, border:"1.5px solid #e5e7eb", background:"#fff", color:"#374151", fontSize:13, cursor:"pointer" }}>← Back</button>
                <button onClick={() => setStep("select")} style={{ padding:"9px 16px", borderRadius:10, border:"1.5px solid #bfdbfe", background:"#eff6ff", color:"#1e40af", fontSize:13, cursor:"pointer", fontWeight:600 }}>📎 Append instead</button>
                <button onClick={handleCreate} disabled={saving || !editedTask.taskName.trim()}
                  style={{ padding:"9px 22px", borderRadius:10, border:"none", background:"linear-gradient(135deg,#059669,#10b981)", color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  {saving ? "Saving..." : "✅ Create Draft Task"}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: SELECT (append) ── */}
          {step === "select" && (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={{ background:"#f8fafc", borderRadius:10, padding:"10px 14px", fontSize:12, color:"#374151", borderLeft:"3px solid #7c3aed" }}>
                <strong style={{ display:"block", marginBottom:3, color:"#7c3aed" }}>Appending context:</strong>
                "{userInput.slice(0, 100)}{userInput.length > 100 ? "..." : ""}"
              </div>
              <p style={{ margin:0, fontSize:12, fontWeight:700, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.05em" }}>Select task to append to:</p>
              {myActiveTasks.length === 0 ? (
                <div style={{ textAlign:"center", padding:"20px", color:"#9ca3af", background:"#f9fafb", borderRadius:10 }}>
                  <p style={{ margin:0, fontSize:13 }}>No active tasks found</p>
                  <button onClick={() => setStep("review")} style={{ marginTop:10, padding:"8px 16px", borderRadius:8, border:"none", background:"#1e40af", color:"#fff", fontSize:12, cursor:"pointer" }}>Create new task instead</button>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:200, overflowY:"auto" }}>
                  {myActiveTasks.map(t => (
                    <label key={t.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:9, cursor:"pointer", background: selTask===t.id?"#eff6ff":"#f8fafc", border:`1.5px solid ${selTask===t.id?"#bfdbfe":"#f0f0f0"}`, transition:"all .1s" }}>
                      <input type="radio" name="selTask" value={t.id} checked={selTask===t.id} onChange={() => setSelTask(t.id)} style={{ accentColor:"#2563eb", flexShrink:0 }}/>
                      <div style={{ flex:1, minWidth:0 }}>
                        <p style={{ margin:0, fontSize:13, fontWeight:600, color:"#111", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.taskName}</p>
                        <p style={{ margin:0, fontSize:11, color:"#9ca3af" }}>{t.status} · {t.projectName || "No project"} · {t.assignedTo}</p>
                      </div>
                      {selTask===t.id && <span style={{ fontSize:14, color:"#2563eb", flexShrink:0 }}>✓</span>}
                    </label>
                  ))}
                </div>
              )}
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:4 }}>
                <button onClick={() => setStep(generated ? "review" : "input")} style={{ padding:"9px 16px", borderRadius:10, border:"1.5px solid #e5e7eb", background:"#fff", color:"#374151", fontSize:13, cursor:"pointer" }}>← Back</button>
                <button onClick={handleAppend} disabled={!selTask || saving}
                  style={{ padding:"9px 22px", borderRadius:10, border:"none", background: selTask?"linear-gradient(135deg,#7c3aed,#a855f7)":"#e5e7eb", color:"#fff", fontWeight:700, fontSize:13, cursor: selTask?"pointer":"not-allowed" }}>
                  {saving ? "Appending..." : "📎 Append Context"}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: DONE ── */}
          {step === "done" && (
            <div style={{ textAlign:"center", padding:"24px 0" }}>
              <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
              <p style={{ margin:"0 0 6px", fontSize:16, fontWeight:800, color:"#059669" }}>Done!</p>
              <p style={{ margin:0, fontSize:13, color:"#9ca3af" }}>Task saved. Closing...</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

/* ── Floating AI Capture Button ── */
function AIFloatingButton({ onClick, aiEnabled, onToggle }) {
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (!aiEnabled) return;
    const t = setInterval(() => { setPulse(true); setTimeout(() => setPulse(false), 600); }, 8000);
    return () => clearInterval(t);
  }, [aiEnabled]);

  return (
    <div style={{ position:"fixed", bottom:24, right:24, zIndex:9000, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8 }}>
      {/* Toggle label */}
      <button onClick={onToggle}
        style={{ background: aiEnabled?"#f0fdf4":"#fef2f2", border:`1px solid ${aiEnabled?"#a7f3d0":"#fecaca"}`, borderRadius:20, padding:"3px 10px", fontSize:10, fontWeight:700, color: aiEnabled?"#059669":"#dc2626", cursor:"pointer" }}>
        🤖 AI {aiEnabled?"ON":"OFF"}
      </button>
      {/* Main button */}
      <button onClick={onClick}
        style={{
          width:56, height:56, borderRadius:"50%", border:"none", cursor:"pointer",
          background:"linear-gradient(135deg,#1e40af,#7c3aed)",
          color:"#fff", fontSize:24, display:"flex", alignItems:"center", justifyContent:"center",
          boxShadow:"0 4px 20px rgba(124,58,237,.4)",
          transform: pulse?"scale(1.12)":"scale(1)",
          transition:"transform .2s, box-shadow .2s",
        }}>
        ✨
      </button>
    </div>
  );
}

function StatsBar({ stats }) {
  return (
    <div className="tf-stats">
      {[
        { label: "Total",      value: stats.total,      color: "#2563eb", bg: "#eff6ff" },
        { label: "Draft",      value: stats.draft,      color: "#78716c", bg: "#fafaf9" },
        { label: "In Progress",value: stats.inProgress, color: "#7c3aed", bg: "#f5f3ff" },
        { label: "On Hold",    value: stats.hold,       color: "#b45309", bg: "#fffbeb" },
        { label: "Completed",  value: stats.done,       color: "#059669", bg: "#f0fdf4" },
        { label: "Overdue",    value: stats.overdue,    color: "#dc2626", bg: "#fef2f2" },
      ].map(s => (
        <div key={s.label} style={{ background:"#fff", borderRadius:14, padding:"14px 16px", border:"1.5px solid #f0f0f0", display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:40, height:40, borderRadius:10, background:s.bg, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:18, color:s.color, flexShrink:0 }}>{s.value}</div>
          <p style={{ margin:0, fontSize:11, color:"#9ca3af", fontWeight:600, lineHeight:1.3 }}>{s.label}</p>
        </div>
      ))}
    </div>
  );
}

/* Login UI */
function LoginUI({ users, onLogin }) {
  const [email,        setEmail]        = useState("");
  const [pass,         setPass]         = useState("");
  const [workLocation, setWorkLocation] = useState("");
  const [err,          setErr]          = useState("");
  const [busy,         setBusy]         = useState(false);
  const [showP,        setShowP]        = useState(false);

  const login = async () => {
    if (!workLocation) { setErr("Please select your work location before signing in."); return; }
    setErr(""); setBusy(true);
    await new Promise(r => setTimeout(r, 380));
    if (email === ADMIN.email && pass === ADMIN.password) { onLogin(ADMIN, workLocation); setBusy(false); return; }
    const u = users.find(u => u.email === email && u.password === pass && u.active);
    if (u) onLogin(u, workLocation);
    else setErr("Invalid email or password, or account is inactive.");
    setBusy(false);
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#060d1f", padding:"16px", position:"relative", overflow:"hidden" }}>
      <style>{GLOBAL_CSS}</style>
      {[["12%","8%","#1d4ed822","5s"],["68%","55%","#7c3aed18","7s"],["38%","76%","#05966918","4s"]].map(([l,t,c,d],i) => (
        <div key={i} style={{ position: "absolute", left: l, top: t, width: 420, height: 420, borderRadius: "50%", background: c, filter: "blur(88px)", animation: `drift ${d} ease-in-out infinite`, animationDelay: `${i*1.6}s` }} />
      ))}
      <div style={{ width:"100%", maxWidth:420, position:"relative", animation:"popIn .3s ease", padding:"0 4px" }}>
        <div style={{ background:"rgba(255,255,255,.04)", backdropFilter:"blur(28px)", border:"1px solid rgba(255,255,255,.08)", borderRadius:24, padding:"clamp(24px,6vw,44px) clamp(20px,5vw,36px)", boxShadow:"0 40px 80px rgba(0,0,0,.5)" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ width: 62, height: 62, borderRadius: 18, background: "linear-gradient(135deg,#1e40af,#3b82f6)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", boxShadow: "0 8px 32px rgba(59,130,246,.45)" }}>
              <TaskIcon size={30} color="#fff" />
            </div>
            <h1 style={{ color: "#fff", fontSize: 30, fontWeight: 800, margin: "0 0 6px", letterSpacing: "-0.5px" }}>TaskFlow</h1>
            <p style={{ color: "rgba(255,255,255,.4)", fontSize: 13, margin: 0 }}>Sign in to your workspace</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={{ display: "block", color: "rgba(255,255,255,.55)", fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.07em" }}>Email Address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && login()} placeholder="you@company.com"
                style={{ width: "100%", padding: "12px 16px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,.1)", background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ display: "block", color: "rgba(255,255,255,.55)", fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.07em" }}>Password</label>
              <div style={{ position: "relative" }}>
                <input type={showP ? "text" : "password"} value={pass} onChange={e => setPass(e.target.value)} onKeyDown={e => e.key === "Enter" && login()} placeholder="••••••••"
                  style={{ width: "100%", padding: "12px 44px 12px 16px", borderRadius: 12, border: "1.5px solid rgba(255,255,255,.1)", background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                <button type="button" onClick={() => setShowP(p => !p)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
                  {showP ? <EyeOffIcon size={16} color="rgba(255,255,255,.4)" /> : <EyeIcon size={16} color="rgba(255,255,255,.4)" />}
                </button>
              </div>
            </div>
            {/* Work Location — required field */}
            <div>
              <label style={{ display: "block", color: "rgba(255,255,255,.55)", fontSize: 11, fontWeight: 700, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Work Location <span style={{ color: "#f59e0b" }}>*</span>
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                {[["🏢", "Office"], ["🏠", "Work From Home"]].map(([icon, label]) => (
                  <button key={label} type="button" onClick={() => setWorkLocation(label)}
                    style={{
                      flex: 1, padding: "11px 8px", borderRadius: 12, cursor: "pointer", fontFamily: "inherit",
                      border: workLocation === label ? "2px solid #3b82f6" : "1.5px solid rgba(255,255,255,.15)",
                      background: workLocation === label ? "rgba(59,130,246,.25)" : "rgba(255,255,255,.06)",
                      color: workLocation === label ? "#93c5fd" : "rgba(255,255,255,.55)",
                      fontWeight: workLocation === label ? 700 : 500, fontSize: 13,
                      transition: "all .15s",
                    }}>
                    <div style={{ fontSize: 20, marginBottom: 3 }}>{icon}</div>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {err && <div style={{ background: "rgba(239,68,68,.15)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 10, padding: "10px 14px", color: "#fca5a5", fontSize: 13, marginTop: 12 }}>{err}</div>}
          <button onClick={login} disabled={busy} style={{ width: "100%", marginTop: 20, padding: "14px", borderRadius: 12, border: "none", background: "linear-gradient(135deg,#1e40af,#3b82f6)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? .7 : 1, fontFamily: "inherit", boxShadow: "0 6px 24px rgba(59,130,246,.45)" }}>
            {busy ? "Signing in..." : "Sign In"}
          </button>
          <p style={{ textAlign: "center", color: "rgba(255,255,255,.22)", fontSize: 11, marginTop: 18 }}>Contact your admin for login credentials</p>
        </div>
      </div>
    </div>
  );
}

/* Root App */
export default function App() {
  const APP_URL = window.location.origin;

  useEffect(() => { document.title = "AV Group Task Flow"; }, []);

  const [currentUser,    setCurrentUser]    = useState(() => loadSession());
  const [users,          setUsers]          = useState([]);
  const [tasks,          setTasks]          = useState([]);
  const [projects,       setProjects]       = useState([]);
  const [attendance,     setAttendance]     = useState([]);
  const [screenIndex,    setScreenIndex]    = useState(new Set());
  const [aiEnabled,       setAiEnabled]      = useState(true);
  const [showAICapture,   setShowAICapture]  = useState(false);
  const [electronPrefill, setElectronPrefill]= useState(null); // context from Electron overlay
  const isElectron = !!(window.taskflowBridge?.isElectron);
  const [loading,        setLoading]        = useState(true);
  const [adminTab,       setAdminTab]       = useState("tasks");
  const [showTaskModal,  setShowTaskModal]  = useState(false);
  const [editingTask,    setEditingTask]    = useState(null);
  const [shareTask,      setShareTask]      = useState(null);
  const [showQuickAdd,   setShowQuickAdd]   = useState(false);
  const [showProfile,    setShowProfile]    = useState(false);
  const [viewMode,       setViewMode]       = useState("table");
  const [filterStatus,   setFilterStatus]   = useState("All");
  const [filterPriority, setFilterPriority] = useState("All");
  const [filterProject,  setFilterProject]  = useState("All");
  const [filterUser,     setFilterUser]     = useState("All");
  const [selectedDate,   setSelectedDate]   = useState("");
  const [search,         setSearch]         = useState("");

  useEffect(() => {
    const unsubUsers       = DB.listen("users",       data => setUsers(data));
    const unsubTasks       = DB.listen("tasks",       data => { setTasks(data); setLoading(false); });
    const unsubProjects    = DB.listen("projects",    data => setProjects(data));
    const unsubAttendance  = DB.listen("attendance",  data => setAttendance(data));
    const unsubScreenIndex = DB.listen("screenIndex", data => {
      setScreenIndex(new Set(data.map(r => r.id)));
    });
    return () => { unsubUsers(); unsubTasks(); unsubProjects(); unsubAttendance(); unsubScreenIndex(); };
  }, []);

  // ── Force-logout: admin can remotely log out any user ──
  // When admin sets forceLogout=true on a user doc, that user is
  // signed out on their next render cycle.
  useEffect(() => {
    if (!currentUser || currentUser.id === "admin") return;
    const liveUser = users.find(u => u.id === currentUser.id);
    if (liveUser?.forceLogout === true) {
      clearSession();
      setCurrentUser(null);
    }
  }, [users, currentUser]);

  // ── Electron desktop bridge ──────────────────────────────
  // When running as Electron desktop app, receive context from the
  // main process (active window detection) and auto-open capture panel
  useEffect(() => {
    const bridge = window.taskflowBridge;
    if (!bridge?.isElectron || !currentUser) return;

    bridge.onTaskCaptured((data) => {
      if (data.autoSave && data.task) {
        const now = nowISO();
        const t = {
          id:             `task-${uid()}`,
          taskId:         `TASK-${String(Date.now()).slice(-6)}`,
          taskName:       data.task.taskName,
          description:    data.task.description,
          projectName:    "",
          assignedTo:     currentUser.name,
          priority:       data.task.priority || "Medium",
          status:         "Draft",
          date:           today(),
          createdBy:      currentUser.id,
          createdByName:  currentUser.name,
          startTime:      now,
          createdAt:      now,
          endTime:        null,
          pausedDuration: 0,
          draftDuration:  0,
          draftStartTime: now,
          holdStartTime:  null,
          subAssigned:    [],
          attachments:    [],
          aiGenerated:    true,
          aiSourceApp:    data.task.appName || "",
        };
        DB.set("tasks", t.id, t);
      } else if (data.prefill) {
        setElectronPrefill(data.prefill);
        setShowAICapture(true);
      }
    });

    return () => { bridge.removeAllListeners?.("task-captured"); };
  }, [currentUser]); // eslint-disable-line

  // ── Reusable attendance writer ──────────────────────────
  // One record per user per date (composite key userId_date).
  // Merges sources so login + task activity both get credited.
  // loginTime is only written if provided (login event), never overwritten by task events.
  const markAttendance = async (userId, userName, dateStr, opts = {}) => {
    if (!userId || userId === "admin" || !dateStr) return;
    const attId = `${userId}_${dateStr}`;
    const record = {
      id:           attId,
      userId,
      userName:     userName || "",
      date:         dateStr,
      lastActivity: nowISO(),
      ...(opts.loginTime    ? { loginTime:    opts.loginTime    } : {}),
      ...(opts.location     ? { location:     opts.location     } : {}),
      ...(opts.workLocation ? { workLocation: opts.workLocation } : {}),
      sources: { [opts.source || "task"]: true },
    };
    await DB.set("attendance", attId, record);
  };

  // ── Back-fill past task records into attendance ─────────
  // Runs once per session after tasks + users are loaded.
  // Writes attendance records for every task's date (creator + assignee).
  const [backfillDone, setBackfillDone] = useState(false);
  useEffect(() => {
    if (backfillDone || tasks.length === 0 || users.length === 0) return;
    setBackfillDone(true);
    const userMap = {};
    users.forEach(u => { userMap[u.id] = u; userMap[u.name] = u; });
    const seen = new Set();
    const writes = [];
    tasks.forEach(t => {
      // Skip Draft and Hold tasks — they don't count as attendance
      if (t.status === "Draft" || t.status === "Hold") return;
      const date = t.date || (t.createdAt ? t.createdAt.slice(0, 10) : null);
      if (!date) return;
      // creator
      if (t.createdBy && t.createdBy !== "admin") {
        const k = `${t.createdBy}_${date}`;
        if (!seen.has(k)) {
          seen.add(k);
          const uName = t.createdByName || userMap[t.createdBy]?.name || "";
          writes.push(markAttendance(t.createdBy, uName, date, { source: "task" }));
        }
      }
      // assignee
      if (t.assignedTo) {
        const au = userMap[t.assignedTo];
        if (au && au.id !== "admin") {
          const k2 = `${au.id}_${date}`;
          if (!seen.has(k2)) {
            seen.add(k2);
            writes.push(markAttendance(au.id, au.name, date, { source: "task" }));
          }
        }
      }
    });
    // fire all writes in parallel (batched)
    Promise.all(writes).catch(() => {});
  }, [tasks.length, users.length, backfillDone]); // eslint-disable-line

  const handleLogin = (user, workLocation = "") => {
    const tryGeo = (cb) => {
      if (!navigator?.geolocation) return cb(null);
      navigator.geolocation.getCurrentPosition(
        pos => {
          const { latitude, longitude } = pos.coords;
          fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`)
            .then(r => r.json())
            .then(d => {
              const a = d.address || {};
              const loc = [a.city || a.town || a.village || a.county, a.country].filter(Boolean).join(", ");
              cb(loc || `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
            })
            .catch(() => cb(`${latitude.toFixed(2)}, ${longitude.toFixed(2)}`));
        },
        () => cb(null),
        { timeout: 4000, maximumAge: 300000 }
      );
    };

    tryGeo(async (loc) => {
      const loginTime = nowISO();
      const dateStr   = loginTime.slice(0, 10);

      const enriched = {
        ...user,
        workLocation,
        ...(loc ? { location: loc } : {}),
      };
      saveSession(enriched);
      setCurrentUser(enriched);

      if (user.id === "admin") return;

      // Clear any stale force-logout flag on login
      await DB.set("users", user.id, { ...user, forceLogout: false, workLocation, ...(loc ? { location: loc } : {}) });

      await markAttendance(user.id, user.name, dateStr, {
        source: "login",
        loginTime,
        workLocation,
        ...(loc ? { location: loc } : {}),
      });
    });
  };
  const handleLogout = () => { clearSession(); setCurrentUser(null); };

  // ── AI Capture helpers ───────────────────────────────
  const updateTaskDirect = async (id, data) => {
    await DB.set("tasks", id, data);
  };

  const saveTask = async (data) => {
    // CRITICAL: always use existing id for edits — never generate a new one if id present
    const t = { ...data, id: data.id || `task-${uid()}` };

    // ── Hold / Draft / timing logic ──────────────────────
    const now = nowISO();
    const prevTask = tasks.find(x => x.id === t.id);
    const prevStatus = prevTask?.status || "Draft";

    // Track when task leaves Draft — accumulate draftDuration
    if (prevStatus === "Draft" && t.status !== "Draft") {
      const draftStart = t.draftStartTime || t.startTime || t.createdAt || now;
      t.draftDuration = (t.draftDuration || 0) + (new Date(now) - new Date(draftStart));
      t.draftStartTime = null;
      // Real work starts now
      t.startTime = now;
    }
    // If creating new as Draft, mark draftStartTime
    if (!prevTask && t.status === "Draft") {
      t.draftStartTime = now;
    }
    // If going back to Draft from another status — start tracking draft time again
    if (prevStatus !== "Draft" && t.status === "Draft") {
      t.draftStartTime = now;
      t.endTime = null;
    }

    if (t.status === "Completed") {
      if (t.holdStartTime) {
        t.pausedDuration = (t.pausedDuration || 0) + (new Date(now) - new Date(t.holdStartTime));
        t.holdStartTime  = null;
      }
      if (!t.endTime) t.endTime = now;
    } else if (t.status === "Hold") {
      if (!t.holdStartTime) t.holdStartTime = now;
      t.endTime = null;
    } else if (t.status !== "Draft") {
      if (t.holdStartTime) {
        t.pausedDuration = (t.pausedDuration || 0) + (new Date(now) - new Date(t.holdStartTime));
        t.holdStartTime  = null;
      }
      t.endTime = null;
    }

    // ── If this task is set to In Progress, put other In Progress tasks on Hold ──
    if (t.status === "In Progress") {
      const otherInProgress = tasks.filter(
        other => other.id !== t.id && other.status === "In Progress" &&
                 other.assignedTo === t.assignedTo
      );
      for (const other of otherInProgress) {
        const holdNow = nowISO();
        const held = { ...other, status: "Hold", holdStartTime: holdNow, endTime: null };
        await DB.set("tasks", other.id, held);
      }
    }

    await DB.set("tasks", t.id, t);

    // ── Attendance — skip Draft tasks ────────────────────
    if (t.status !== "Draft") {
      const taskDate = t.date || t.createdAt?.slice(0, 10) || now.slice(0, 10);
      if (t.createdBy && t.createdBy !== "admin") {
        await markAttendance(t.createdBy, t.createdByName || "", taskDate, { source: "task" });
      }
      if (t.assignedTo) {
        const au = users.find(u => u.name === t.assignedTo);
        if (au && au.id !== "admin") await markAttendance(au.id, au.name, taskDate, { source: "task" });
      }
    }
    setShowTaskModal(false); setEditingTask(null);
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await DB.del("tasks", id);
  };

  const updateStatus = async (id, status) => {
    const t = tasks.find(t => t.id === id); if (!t) return;
    const now = nowISO();
    const updates = { ...t, status };
    const prevStatus = t.status;

    // ── Draft transition ─────────────────────────────────
    if (prevStatus === "Draft" && status !== "Draft") {
      // Leaving draft — accumulate draft duration, real work starts now
      const draftStart = updates.draftStartTime || updates.startTime || updates.createdAt || now;
      updates.draftDuration = (updates.draftDuration || 0) + (new Date(now) - new Date(draftStart));
      updates.draftStartTime = null;
      updates.startTime = now; // real clock starts
    }
    if (prevStatus !== "Draft" && status === "Draft") {
      // Going back to draft
      updates.draftStartTime = now;
      updates.endTime = null;
    }

    // ── Hold / Completed timing ──────────────────────────
    if (status === "Completed") {
      if (updates.holdStartTime) {
        updates.pausedDuration = (updates.pausedDuration || 0) + (new Date(now) - new Date(updates.holdStartTime));
        updates.holdStartTime  = null;
      }
      if (!updates.endTime) updates.endTime = now;
    } else if (status === "Hold") {
      if (!updates.holdStartTime) updates.holdStartTime = now;
      updates.endTime = null;
    } else if (status !== "Draft") {
      if (updates.holdStartTime) {
        updates.pausedDuration = (updates.pausedDuration || 0) + (new Date(now) - new Date(updates.holdStartTime));
        updates.holdStartTime  = null;
      }
      updates.endTime = null;
    }

    // ── Auto-Hold other In Progress tasks for same assignee ──
    if (status === "In Progress") {
      const otherInProgress = tasks.filter(
        other => other.id !== id && other.status === "In Progress" && other.assignedTo === t.assignedTo
      );
      for (const other of otherInProgress) {
        await DB.set("tasks", other.id, { ...other, status: "Hold", holdStartTime: now, endTime: null });
      }
    }

    await DB.set("tasks", id, updates);

    // ── Attendance — skip if task stays in Draft or Hold ─
    if (status !== "Draft" && status !== "Hold") {
      if (currentUser?.id && currentUser.id !== "admin") {
        await markAttendance(currentUser.id, currentUser.name, now.slice(0, 10), { source: "task" });
      }
      const taskDate = t.date || t.createdAt?.slice(0, 10);
      if (taskDate) {
        if (t.createdBy && t.createdBy !== "admin")
          await markAttendance(t.createdBy, t.createdByName || "", taskDate, { source: "task" });
        if (t.assignedTo) {
          const au = users.find(u => u.name === t.assignedTo);
          if (au && au.id !== "admin") await markAttendance(au.id, au.name, taskDate, { source: "task" });
        }
      }
    }
  };

  const isAdmin      = currentUser?.role === "admin";
  const regularUsers = users.filter(u => u.active);

  const visibleTasks = useCallback(() => tasks.filter(t => {
    if (!isAdmin && t.assignedTo !== currentUser?.name && t.createdBy !== currentUser?.id) return false;
    if (filterStatus   !== "All" && t.status      !== filterStatus)   return false;
    if (filterPriority !== "All" && t.priority    !== filterPriority) return false;
    if (filterProject  !== "All" && t.projectName !== filterProject)  return false;
    if (isAdmin && filterUser !== "All" && t.assignedTo !== filterUser) return false;
    if (selectedDate && t.date !== selectedDate) return false;
    if (search) {
      const q = search.toLowerCase();
      if (![t.taskName, t.projectName, t.taskId, t.assignedTo, t.description].some(s => (s || "").toLowerCase().includes(q))) return false;
    }
    return true;
  }), [tasks, isAdmin, currentUser, filterStatus, filterPriority, filterProject, filterUser, selectedDate, search]);

  const taskList = visibleTasks();
  const statsSrc = isAdmin ? tasks : tasks.filter(t => t.assignedTo === currentUser?.name || t.createdBy === currentUser?.id);
  const stats    = {
    total:      statsSrc.length,
    draft:      statsSrc.filter(t => t.status === "Draft").length,
    inProgress: statsSrc.filter(t => t.status === "In Progress").length,
    hold:       statsSrc.filter(t => t.status === "Hold").length,
    done:       statsSrc.filter(t => t.status === "Completed").length,
    overdue:    statsSrc.filter(isOver).length,
  };
  const canEdit = (t) => isAdmin || t.assignedTo === currentUser?.name || t.createdBy === currentUser?.id;

  if (loading) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#f5f6fa" }}>
      <style>{GLOBAL_CSS}</style>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:40, height:40, border:"3px solid #e5e7eb", borderTopColor:"#3b82f6", borderRadius:"50%", margin:"0 auto 12px", animation:"spin .7s linear infinite" }} />
        <p style={{ color:"#9ca3af", fontSize:13 }}>Connecting to Firebase...</p>
      </div>
    </div>
  );

  if (!currentUser) return <LoginUI users={users} onLogin={handleLogin} />;

  return (
    <div style={{ minHeight:"100vh", background:"#f5f6fa" }}>
      <style>{GLOBAL_CSS}</style>

      <header style={{ background:"#fff", borderBottom:"1.5px solid #f0f0f0", position:"sticky", top:0, zIndex:200 }}>
        <div className="tf-header-inner">
          <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
            <div style={{ width:34, height:34, borderRadius:10, background:"linear-gradient(135deg,#1e40af,#3b82f6)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <TaskIcon size={17} color="#fff" />
            </div>
            <span style={{ fontSize:17, fontWeight:800, color:"#0f172a", letterSpacing:"-0.3px" }}>TaskFlow</span>
            {isAdmin && <span style={{ background:"linear-gradient(135deg,#dc2626,#f59e0b)", borderRadius:6, padding:"2px 7px", fontSize:10, fontWeight:700, color:"#fff" }}>ADMIN</span>}
            <span style={{ background:"#f0fdf4", border:"1px solid #a7f3d0", borderRadius:6, padding:"2px 7px", fontSize:10, fontWeight:700, color:"#059669" }}>LIVE</span>
          </div>

          {isAdmin && (
            <div className="tf-nav">
              {[["tasks","📋 Tasks"],["users","👥 Members"],["projects","📁 Projects"],["attendance","🗓 Attendance"]].map(([k,l]) => (
                <button key={k} onClick={() => setAdminTab(k)} style={{ padding:"7px 14px", borderRadius:8, border:"none", background:adminTab===k?"#fff":"transparent", color:adminTab===k?"#1e40af":"#6b7280", fontWeight:adminTab===k?700:500, fontSize:13, cursor:"pointer", boxShadow:adminTab===k?"0 1px 4px rgba(0,0,0,.08)":"none", whiteSpace:"nowrap" }}>{l}</button>
              ))}
            </div>
          )}

          <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
            <Avatar name={currentUser.name} size={32} />
            <div style={{ display:"none" }} className="tf-username">
              <p style={{ margin:0, fontSize:13, fontWeight:700, color:"#111" }}>{currentUser.name}</p>
              <p style={{ margin:0, fontSize:11, color:"#9ca3af" }}>{isAdmin?"Administrator":"Team Member"}</p>
              {currentUser.location && (
                <p style={{ margin:0, fontSize:10, color:"#6b7280", display:"flex", alignItems:"center", gap:3 }}>📍 {currentUser.location}</p>
              )}
            </div>
            <button onClick={() => setShowProfile(true)} title="Edit Profile"
              style={{ background:"#f0f9ff", border:"none", borderRadius:8, padding:"7px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
              <span style={{ fontSize:15 }}>✏️</span>
              <span style={{ fontSize:12, color:"#2563eb", fontWeight:600, display:"none" }} className="tf-username-label">Profile</span>
            </button>
            <button onClick={handleLogout} style={{ background:"#fef2f2", border:"none", borderRadius:8, padding:"7px 10px", cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
              <LogoutIcon size={15} color="#dc2626" />
              <span style={{ fontSize:12, color:"#dc2626", fontWeight:600 }}>Out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="tf-main">
        {isAdmin && adminTab === "users" ? (
          <UserManagement users={users.filter(u => u.id !== "admin")} tasks={tasks} appUrl={APP_URL} onUsersChange={setUsers} />
        ) : isAdmin && adminTab === "projects" ? (
          <ProjectManagement projects={projects} onProjectsChange={setProjects} />
        ) : isAdmin && adminTab === "attendance" ? (
          <AttendancePanel users={users} tasks={tasks} attendance={attendance} currentUser={currentUser} />
        ) : (
          <>
            <StatsBar stats={stats} />

            <div className="tf-toolbar">
              <div style={{ display: "flex", alignItems: "center", gap: 7, flex: "1 1 160px", background: "#f5f6fa", borderRadius: 10, padding: "8px 12px" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} style={{ border: "none", background: "none", outline: "none", fontSize: 13, color: "#374151", flex: 1, fontFamily: "inherit" }} />
              </div>
              <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                style={{ padding: "8px 11px", borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: 13, color: "#374151", background: "#f9fafb", outline: "none", fontFamily: "inherit", cursor: "pointer" }} />
              {[
                { v: filterStatus,   s: setFilterStatus,   opts: ["All","Draft","To-Do","In Progress","Hold","Review","Completed"] },
                { v: filterPriority, s: setFilterPriority, opts: ["All","High","Medium","Low"] },
              ].map((x, i) => (
                <select key={i} value={x.v} onChange={e => x.s(e.target.value)}
                  style={{ padding: "8px 11px", borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: 13, color: "#374151", background: "#f9fafb", outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
                  {x.opts.map(o => <option key={o}>{o}</option>)}
                </select>
              ))}
              <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
                style={{ padding: "8px 11px", borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: 13, color: "#374151", background: "#f9fafb", outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
                <option value="All">All Projects</option>
                {[...projects].sort((a,b) => a.name.localeCompare(b.name)).map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
              {isAdmin && (
                <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
                  style={{ padding: "8px 11px", borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: 13, color: "#374151", background: "#f9fafb", outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
                  <option value="All">All Members</option>
                  {[...regularUsers].sort((a,b) => a.name.localeCompare(b.name)).map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                </select>
              )}
              {isAdmin && (
                <Btn onClick={() => exportCSV(taskList)} variant="success" size="md">
                  <DownloadIcon size={14} color="#fff" /> Export CSV
                </Btn>
              )}
              <Btn onClick={() => setShareTask("multi")} variant="ghost" sx={{ border: "1.5px solid #e5e7eb" }}>
                <ShareIcon size={14} color="#374151" /> Share Multiple
              </Btn>
              <Btn onClick={() => setShowQuickAdd(q => !q)} variant="purple">
                <ZapIcon size={14} color="#fff" /> Quick Add
              </Btn>
              <Btn onClick={() => { setEditingTask(null); setShowTaskModal(true); }} variant="primary">
                <PlusIcon size={14} color="#fff" /> New Task
              </Btn>
              <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 8, padding: 2, gap: 2 }}>
                <button onClick={() => setViewMode("table")} style={{ padding: "6px 10px", borderRadius: 6, border: "none", cursor: "pointer", background: viewMode === "table" ? "#fff" : "transparent", boxShadow: viewMode === "table" ? "0 1px 3px rgba(0,0,0,.1)" : "none" }}>
                  <TableIcon size={14} color={viewMode === "table" ? "#1e40af" : "#9ca3af"} />
                </button>
                <button onClick={() => setViewMode("grid")} style={{ padding: "6px 10px", borderRadius: 6, border: "none", cursor: "pointer", background: viewMode === "grid" ? "#fff" : "transparent", boxShadow: viewMode === "grid" ? "0 1px 3px rgba(0,0,0,.1)" : "none" }}>
                  <GridIcon size={14} color={viewMode === "grid" ? "#1e40af" : "#9ca3af"} />
                </button>
              </div>
            </div>

            {showQuickAdd && (
              <QuickAdd projects={projects} users={regularUsers} currentUser={currentUser} onSaved={() => setShowQuickAdd(false)} onClose={() => setShowQuickAdd(false)} />
            )}

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div>
                <h2 style={{ margin: "0 0 2px", fontSize: 17, fontWeight: 700, color: "#111" }}>
                  {selectedDate ? `Tasks - ${fmtDate(selectedDate)}` : "All Tasks"}
                </h2>
                <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>{taskList.length} task{taskList.length !== 1 ? "s" : ""} - synced live from Firebase</p>
              </div>
              {!isAdmin && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#eff6ff", borderRadius: 10, padding: "8px 14px" }}>
                  <Avatar name={currentUser.name} size={26} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#1e40af" }}>{currentUser.name}</span>
                </div>
              )}
            </div>

            {viewMode === "table" ? (
              <TaskTable tasks={taskList} canEdit={canEdit} onEdit={t => { setEditingTask(t); setShowTaskModal(true); }} onDelete={deleteTask} onShare={t => setShareTask(t)} onStatusChange={updateStatus} onCellChange={(id, data) => updateTaskDirect(id, data)} users={regularUsers} projects={projects} />
            ) : (
              taskList.length === 0 ? (
                <div style={{ background: "#fff", borderRadius: 16, padding: "56px 24px", textAlign: "center", border: "1.5px dashed #e5e7eb" }}>
                  <div style={{ fontSize: 52, marginBottom: 14 }}>📋</div>
                  <h3 style={{ margin: "0 0 6px", color: "#374151", fontWeight: 700 }}>No tasks found</h3>
                  <p style={{ margin: "0 0 20px", color: "#9ca3af", fontSize: 14 }}>Create your first task to get started.</p>
                  <Btn onClick={() => { setEditingTask(null); setShowTaskModal(true); }} variant="primary"><PlusIcon size={14} color="#fff" /> Create Task</Btn>
                </div>
              ) : (
                <div className="tf-grid">
                  {taskList.map(t => (
                    <TaskCard key={t.id} task={t} canEdit={canEdit(t)}
                      onEdit={t => { setEditingTask(t); setShowTaskModal(true); }}
                      onDelete={deleteTask}
                      onShare={t => setShareTask(t)}
                      onStatusChange={updateStatus}
                    />
                  ))}
                </div>
              )
            )}
          </>
        )}
      </main>

      {showTaskModal && (
        <TaskModal task={editingTask}
          users={isAdmin ? regularUsers : [currentUser, ...regularUsers.filter(u => u.id !== currentUser.id)]}
          projects={projects}
          currentUser={currentUser}
          onSave={saveTask}
          onClose={() => { setShowTaskModal(false); setEditingTask(null); }} />
      )}
      {showProfile && currentUser.id !== "admin" && (
        <ProfileModal
          user={currentUser}
          onSave={(updated) => { setCurrentUser(updated); setShowProfile(false); }}
          onClose={() => setShowProfile(false)}
        />
      )}
      {shareTask && <TaskShareModal task={shareTask} allTasks={taskList} onClose={() => setShareTask(null)} />}

      {/* AI Floating Capture Button — visible to all non-admin users */}
      {currentUser.id !== "admin" && (
        <AIFloatingButton
          onClick={() => setShowAICapture(true)}
          aiEnabled={aiEnabled}
          onToggle={() => setAiEnabled(p => !p)}
        />
      )}

      {/* AI Capture Panel */}
      {showAICapture && (
        <AICapturePanel
          tasks={tasks}
          users={users.filter(u => u.active)}
          projects={projects}
          currentUser={currentUser}
          prefill={electronPrefill}
          onSaveTask={async (t) => { await DB.set("tasks", t.id, t); }}
          onUpdateTask={updateTaskDirect}
          onClose={() => { setShowAICapture(false); setElectronPrefill(null); }}
        />
      )}
    </div>
  );
}
